import { useEffect, useMemo, useRef, useState } from "react";
import {
  ApiError,
  analyzeFilmProject,
  buildProductionPackage,
  buildStoryboard,
  deleteCloudProject,
  duplicateProjectPackageVersion,
  generateCharacterCards,
  generatePoster,
  generateSceneReferences,
  getApiHealth,
  listCloudProjects,
  loadCloudProject,
  parseReferenceBoard,
  saveCloudProject,
  saveProjectPackageVersion,
  uploadProjectAsset, createShareSnapshot } from "./api/client";
import type { CloudProjectSummary } from "./api/client";
import { classifyAsset, deleteAssetObject, EXPECTED_ENGINE_FEATURE_LEVEL } from "./api/client";
import { AIParseReview } from "./components/AIParseReview";
import { AIPlanReview } from "./components/AIPlanReview";
import { AppShell } from "./components/AppShell";
import { AssetLibraryDrawer } from "./components/AssetLibraryDrawer";
import { EasyModeProgressPanel } from "./components/EasyModeProgressPanel";
import { FrameStatesLegend } from "./components/FrameStatesLegend";
import { GenerateBoardsSheet } from "./components/GenerateBoardsSheet";
import { ImportAssetsModal } from "./components/ImportAssetsModal";
import { Inspector } from "./components/Inspector";
import { PromptPreviewModal } from "./components/PromptPreviewModal";
import { ProjectIntake } from "./components/ProjectIntake";
import { ProjectLibrary } from "./components/ProjectLibrary";
import { ReviewEvoLinkPanel } from "./components/ReviewEvoLinkPanel";
import { ScriptRail } from "./components/ScriptRail";
import { SimpleMode } from "./components/SimpleMode";
import type { SimpleSceneRemakeRequest } from "./components/SimpleMode";
import { SourceWorkspace } from "./components/SourceWorkspace";
import { ProGlass } from "./components/ProGlass";
import { StoryboardReviewList } from "./components/StoryboardReviewList";
import { StoryboardWorkspace } from "./components/StoryboardWorkspace";
import { Topbar } from "./components/Topbar";
import { sampleProject } from "./data/sampleProject";
import { SAMPLE_SCRIPT_FILE_NAME, SAMPLE_SCRIPT_TEXT } from "./data/sampleScript";
import type {
  ApiStatus,
  BoardGenerationJob,
  BoardGenerationSettings,
  BoardSource,
  EasyModeOptions,
  EasyModeProgress,
  EvoLinkCategory,
  ExportScope,
  FilmProject,
  PackageIntent,
  ParsedReferenceBoard,
  ProductionPackageHistoryItem,
  ProjectScriptUpload,
  ProjectUiState,
  ReviewGateState,
  SaveStatus,
  SceneChunk,
  SourceFocusTarget,
  SourceWorkspaceSection,
  StoryboardFrame,
  StoryboardFrameSourceAssignments,
  ThemeMode,
  UploadedAssetManifest,
  UploadedAssetRecord,
  WorkspaceSection,
  SeedanceVoicePreset
} from "./types";
import {
  analysisToFilmProject,
  categoriesToPackageSettings,
  filmProjectToApiPlan,
  scopedApiPlan,
  scopeToSceneSelection,
  secondsToRuntime,
  storyboardToFilmProject
} from "./utils/projectAdapters";
import { buildPackageSourceMapSnapshot, packageSourceMapFromPackage } from "./utils/packageSourceMap";
import type { MissingInput } from "./utils/analysisSummary";
import { agentPlanSummary, missingInputToPackageRequired, reviewMissingInputsForScenes } from "./utils/analysisSummary";
import { buildSceneChunks, chunkForFrame, frameBlockedByDisabledChunk, framesForScope, sceneChunks, scenesForScope } from "./utils/storyboard";
import {
  applyGeneratedCardsToCharacters,
  applyGeneratedCardsToManifest,
  applyGeneratedScenePlates,
  charactersInScenes,
  createEasyModeProgress,
  dedupeReferenceAssets,
  finishEasyModeProgress,
  patchEasyModeStep,
  planEasyModeVoices,
  referenceAssetsFromManifest,
  resolveImageSize,
  scenesForEasyModeScope
} from "./utils/easyMode";
import { matchPeople, matchPlaces, reconcileCharactersWithUploads } from "./utils/assetMatching";
import { projectLogline, projectTone } from "./utils/scenePage";
import type { GeneratedCardResult, GeneratedScenePlate, ReferenceAsset } from "./utils/easyMode";

const STORAGE_KEY = "kinetix_film_react_project_v1";
const THEME_KEY = "kinetix_film_react_theme_v1";
const EXPERIENCE_KEY = "kinetix_film_react_experience_v1";
const ACCESS_KEY = "kinetix_film_react_access_key_v1";
const WORKSPACE_ID = "workspace_default";
const PROJECT_QUERY_KEY = "project";
const ACCESS_QUERY_KEY = "accessKey";
const PACKAGE_HISTORY_LIMIT = 25;

type WorkflowView = "workspace" | "intake" | "parseReview" | "aiPlan";
type AssetBucket = keyof UploadedAssetManifest;
type RepairReturnTarget = NonNullable<SourceFocusTarget["returnTo"]>;

interface RestoreRequest {
  projectId: string;
  accessKey: string;
}

interface CloudDraftConflict {
  project: FilmProject;
  savedAt: string;
  message: string;
}

interface AssetUploadRetryRecord {
  bucket: AssetBucket;
  file: File;
  context: Partial<UploadedAssetRecord>;
}

interface UploadSyncSummary {
  total: number;
  uploaded: number;
  pending: number;
  failed: number;
  localOnly: number;
}

const defaultBoardSettings: BoardGenerationSettings = {
  scope: "current",
  source: "none",
  stylePreset: "Cinematic Realism",
  panelsPerScene: 6,
  imageQuality: "low",
  imageSize: "auto"
};

const defaultEasyModeOptions: EasyModeOptions = {
  sceneScope: "all",
  imageQuality: "low",
  imageSize: "auto"
};

/** Outcome summary returned by runBoardGeneration so callers (Easy Mode) can report progress. */
interface BoardGenerationOutcome {
  status: "generated" | "failed" | "skipped";
  generatedCount: number;
  failedCount: number;
  targetCount: number;
  error?: string;
}

const defaultCategories: EvoLinkCategory[] = [
  "boards",
  "characters",
  "voices",
  "lines",
  "locations",
  "music",
  "sfx",
  "style",
  "colorGrade",
  "aspect",
  "chunkTiming"
];

const defaultReviewGateState: ReviewGateState = {
  scriptRights: false,
  likenessRights: false,
  voiceRights: false,
  brandIpRights: false,
  humanReview: false,
  finalConfirm: false
};

const sourceBucketLabels: Record<AssetBucket, string> = {
  characterCards: "character cards",
  sceneReferences: "scene references",
  voiceAudio: "voice audio",
  music: "music",
  sfx: "SFX",
  videoReferences: "Video References",
  styleRefs: "Style / AI Refs",
  branding: "Branding & Props",
  documents: "Documents"
};

function emptyAssetManifest(): UploadedAssetManifest {
  return {
    characterCards: [],
    sceneReferences: [],
    voiceAudio: [],
    music: [],
    sfx: [],
    videoReferences: [],
    styleRefs: [],
    branding: [],
    documents: []
  };
}

function uploadedAssetRecords(project: FilmProject): UploadedAssetRecord[] {
  const manifest = project.uploadedAssets || emptyAssetManifest();
  return Object.values(manifest).flat();
}

function uploadSyncSummaryForProject(project: FilmProject): UploadSyncSummary {
  const records = uploadedAssetRecords(project);
  return records.reduce<UploadSyncSummary>((summary, record) => {
    const status = record.cloudStatus || (record.localOnly ? "local_only" : "");
    if (status === "uploaded") summary.uploaded += 1;
    if (status === "pending") summary.pending += 1;
    if (status === "failed") summary.failed += 1;
    if (status === "local_only") summary.localOnly += 1;
    return { ...summary, total: summary.total + 1 };
  }, { total: 0, uploaded: 0, pending: 0, failed: 0, localOnly: 0 });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function recordValue(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {};
}

function stringValue(value: unknown, fallback = ""): string {
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
}

function numberValue(value: unknown, fallback = 0): number {
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number) ? Math.round(number) : fallback;
}

function stringArrayValue(value: unknown): string[] {
  return Array.isArray(value) ? value.map((item) => stringValue(item)).filter(Boolean) : [];
}

function recordArrayValue(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function arrayCountValue(value: unknown): number {
  return Array.isArray(value) ? value.length : 0;
}

function objectStringField(value: unknown, field: string): string {
  if (typeof value !== "string") return "";
  const match = value.match(new RegExp(`["']${field}["']\\s*:\\s*["']([^"']+)["']`));
  return match?.[1]?.trim() || "";
}

function readableBlockerKind(value: unknown, fallback = "source"): string {
  const raw = stringValue(value, fallback);
  return raw.replace(/_/g, " ");
}

function normalizeBlockerLabel(value: unknown): string {
  const text = stringValue(value);
  const label = objectStringField(text, "label");
  if (!label) return text;
  const kind = objectStringField(text, "kind") || objectStringField(text, "category") || "source";
  return `${readableBlockerKind(kind)}: ${label}`;
}

function missingRequiredBlockerLabels(items: Array<{ kind?: unknown; category?: unknown; label?: unknown; reason?: unknown }>): string[] {
  return items
    .map((item) => {
      const labelSource = item.label || item.reason || "";
      const kind = readableBlockerKind(
        item.kind || objectStringField(labelSource, "kind") || item.category,
        "source"
      );
      const label = objectStringField(labelSource, "label")
        || stringValue(labelSource, "Required source");
      return `${kind}: ${label}`;
    })
    .filter((item, index, items) => items.indexOf(item) === index);
}

function packageHistoryId(): string {
  const suffix = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `pkg-${suffix}`.toLowerCase().replace(/[^a-z0-9_-]+/g, "-").slice(0, 96);
}

function scopeLabel(scope: ExportScope): string {
  if (scope === "all") return "Full script";
  if (scope === "beats") return "Trailer beats";
  if (scope === "selected") return "Selected scenes";
  return "Current scene";
}

function createPackageHistoryItem(
  sourcePackage: Record<string, unknown>,
  project: FilmProject,
  scope: ExportScope,
  intent: PackageIntent
): ProductionPackageHistoryItem {
  const fallbackPlan = recordValue(sourcePackage.plan);
  const providerPayload = recordValue(sourcePackage.providerPayload);
  const storyboard = recordValue(sourcePackage.storyboard);
  const runtime = recordValue(sourcePackage.runtime);
  const runtimeChunks = recordArrayValue(runtime.chunks).length
    ? recordArrayValue(runtime.chunks)
    : recordArrayValue(fallbackPlan.chunks);
  const storyboardFrames = recordArrayValue(storyboard.frames).length
    ? recordArrayValue(storyboard.frames)
    : recordArrayValue(fallbackPlan.storyboardFrames);
  const voicePlan = recordValue(sourcePackage.voicePlan);
  const voiceAssignments = recordArrayValue(voicePlan.assignments).length
    ? recordArrayValue(voicePlan.assignments)
    : recordArrayValue(fallbackPlan.voices);
  const missingRequired = recordArrayValue(sourcePackage.missingRequired);
  const blocked = [
    ...stringArrayValue(providerPayload.blocked).map(normalizeBlockerLabel),
    ...missingRequiredBlockerLabels(missingRequired)
  ].filter((item, index, items) => items.indexOf(item) === index);
  const scopeRecord = recordValue(sourcePackage.scope);
  const scopeSceneIds = stringArrayValue(scopeRecord.sceneIds);
  const fallbackSceneIds = recordArrayValue(fallbackPlan.scenes)
    .map((scene) => stringValue(scene.sceneId || scene.id))
    .filter(Boolean);
  const sceneIds = scopeSceneIds.length
    ? scopeSceneIds
    : stringArrayValue(sourcePackage.targetSceneIds).length
      ? stringArrayValue(sourcePackage.targetSceneIds)
      : fallbackSceneIds;
  const runtimeSeconds = numberValue(
    runtime.scopeSeconds || runtime.targetSeconds || runtime.runtimeSeconds,
    runtimeChunks.reduce((total, chunk) => total + numberValue(chunk.seconds || chunk.durationSeconds), 0)
  );
  const resolution = stringValue(sourcePackage.resolution, project.resolution);
  const aspect = stringValue(sourcePackage.aspect, project.aspectRatio);
  const versionNumber = (project.productionPackages?.length || 0) + 1;
  const versionLabel = `v${versionNumber}`;
  const packageKind = intent === "send_test" ? "480p test" : "Dry-run package";
  const sourceMap = packageSourceMapFromPackage(sourcePackage);
  return {
    id: packageHistoryId(),
    createdAt: stringValue(sourcePackage.createdAt, new Date().toISOString()),
    title: `${packageKind} ${versionLabel} · ${scopeLabel(scope)}`,
    versionNumber,
    versionLabel,
    packageIntent: intent,
    status: missingRequired.length ? "missing_required_inputs" : stringValue(sourcePackage.status, "package_ready"),
    ready: providerPayload.ready === true && missingRequired.length === 0 && blocked.length === 0,
    dryRunOnly: sourcePackage.dryRunOnly !== false,
    scopeLabel: scopeLabel(scope),
    sceneIds,
    runtimeSeconds,
    chunkCount: runtimeChunks.length || numberValue(runtime.chunkCount),
    frameCount: storyboardFrames.length,
    voiceCount: voiceAssignments.length,
    characterCount: arrayCountValue(sourcePackage.characters) || arrayCountValue(fallbackPlan.characters),
    missingCount: missingRequired.length,
    blocked,
    output: `${resolution} · ${aspect}`,
    sourceMap,
    package: sourcePackage
  };
}

function withProductionPackageHistory(
  project: FilmProject,
  sourcePackage: Record<string, unknown>,
  scope: ExportScope,
  intent: PackageIntent
): FilmProject {
  const item = createPackageHistoryItem(sourcePackage, project, scope, intent);
  return withProductionPackageHistoryItem(project, item);
}

function withProductionPackageHistoryItem(
  project: FilmProject,
  item: ProductionPackageHistoryItem
): FilmProject {
  return {
    ...project,
    lastProductionPackage: item.package,
    productionPackages: [item, ...(project.productionPackages || []).filter((existing) => existing.id !== item.id)].slice(0, PACKAGE_HISTORY_LIMIT)
  };
}

function duplicateProductionPackageItem(source: ProductionPackageHistoryItem, versionNumber: number): ProductionPackageHistoryItem {
  const createdAt = new Date().toISOString();
  const sourceMap = source.sourceMap || packageSourceMapFromPackage(source.package);
  const duplicatedPackage = {
    ...source.package,
    createdAt,
    duplicatedFromPackageId: source.id,
    sourceMap,
    dryRunOnly: true,
    liveSendEnabled: false,
    providerSpendAllowed: false
  };
  return {
    ...source,
    id: packageHistoryId(),
    createdAt,
    renamedAt: undefined,
    title: `${source.title} copy`,
    versionNumber,
    versionLabel: `v${versionNumber}`,
    sourceMap,
    dryRunOnly: true,
    package: duplicatedPackage
  };
}

function packageVersionReference(item: ProductionPackageHistoryItem): string {
  return item.versionLabel || (item.versionNumber ? `v${item.versionNumber}` : item.id);
}

function readUrlSearchParam(key: string): string {
  try {
    return new URL(window.location.href).searchParams.get(key)?.trim() || "";
  } catch {
    return "";
  }
}

export function normalizeProjectPayload(payload: Partial<FilmProject> | FilmProject | null | undefined): FilmProject {
  const projectLike: Partial<FilmProject> = payload && typeof payload === "object" ? payload : {};
  const uploadedAssets = projectLike.uploadedAssets || sampleProject.uploadedAssets || emptyAssetManifest();
  return {
    ...sampleProject,
    ...projectLike,
    id: String(projectLike.id || sampleProject.id),
    title: String(projectLike.title || sampleProject.title),
    scenes: Array.isArray(projectLike.scenes) ? projectLike.scenes : sampleProject.scenes,
    characters: Array.isArray(projectLike.characters) ? projectLike.characters : sampleProject.characters,
    agentBeats: Array.isArray(projectLike.agentBeats) ? projectLike.agentBeats : sampleProject.agentBeats,
    generationJobs: Array.isArray(projectLike.generationJobs) ? projectLike.generationJobs : sampleProject.generationJobs || [],
    productionPackages: Array.isArray(projectLike.productionPackages) ? projectLike.productionPackages : sampleProject.productionPackages || [],
    uploadedAssets: {
      ...emptyAssetManifest(),
      ...sampleProject.uploadedAssets,
      ...uploadedAssets
    }
  };
}

function loadProject(): FilmProject {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved ? normalizeProjectPayload(JSON.parse(saved)) : sampleProject;
  } catch {
    return sampleProject;
  }
}

function loadTheme(): ThemeMode {
  try {
    return (window.localStorage.getItem(THEME_KEY) as ThemeMode | null) || "light";
  } catch {
    return "light";
  }
}

function loadAccessKey(): string {
  try {
    const urlAccessKey = readUrlSearchParam(ACCESS_QUERY_KEY);
    if (urlAccessKey) {
      window.localStorage.setItem(ACCESS_KEY, urlAccessKey);
      return urlAccessKey;
    }
    const saved = window.localStorage.getItem(ACCESS_KEY);
    if (saved) return saved;
    const generated = `browser_${globalThis.crypto?.randomUUID?.() || `${Date.now()}_${Math.random().toString(16).slice(2)}`}`;
    window.localStorage.setItem(ACCESS_KEY, generated);
    return generated;
  } catch {
    return `browser_${Date.now()}`;
  }
}

function loadRestoreRequest(accessKey: string): RestoreRequest | null {
  const projectId = readUrlSearchParam(PROJECT_QUERY_KEY);
  if (!projectId) return null;
  return {
    projectId,
    accessKey: readUrlSearchParam(ACCESS_QUERY_KEY) || accessKey
  };
}

function cloudUpdatedAtFromResponse(response: { savedAt?: string; meta?: Record<string, unknown> }): string {
  return stringValue(response.savedAt || response.meta?.updatedAt);
}

function cloudDraftConflictFromError(error: unknown): CloudDraftConflict | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const project = error.payload.project;
  if (!isRecord(project)) return null;
  const meta = recordValue(error.payload.meta);
  const savedAt = stringValue(error.payload.savedAt || meta.updatedAt);
  return {
    project: normalizeProjectPayload(project as Partial<FilmProject>),
    savedAt,
    message: stringValue(error.payload.message, "A newer cloud draft exists.")
  };
}

function buildProjectRestoreUrl(projectId: string, accessKey: string): string {
  const url = new URL(window.location.href);
  url.searchParams.set(PROJECT_QUERY_KEY, projectId);
  url.searchParams.set(ACCESS_QUERY_KEY, accessKey);
  return url.toString();
}

function clearProjectRestoreUrl(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete(PROJECT_QUERY_KEY);
  url.searchParams.delete(ACCESS_QUERY_KEY);
  window.history.replaceState(null, "", url.toString());
}

function generateProjectId(prefix = "project"): string {
  const suffix = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}-${suffix}`.toLowerCase().replace(/[^a-z0-9_-]+/g, "-").slice(0, 96);
}

function isWorkspaceSection(value: unknown): value is WorkspaceSection {
  return value === "projects" || value === "storyboards" || value === "characters" || value === "voices" || value === "references";
}

function isExportScope(value: unknown): value is ExportScope {
  return value === "current" || value === "selected" || value === "beats" || value === "all";
}

function activeSceneIdForProject(project: FilmProject, preferredSceneId?: string): string {
  return project.scenes.find((scene) => scene.id === preferredSceneId)?.id || project.scenes[0]?.id || "";
}

function activeFrameIdForProject(project: FilmProject, sceneId: string, preferredFrameId?: string): string {
  const scene = project.scenes.find((item) => item.id === sceneId) || project.scenes[0];
  return scene?.frames.find((frame) => frame.id === preferredFrameId)?.id || scene?.frames[0]?.id || "";
}

function boardSettingsForProject(project: FilmProject): BoardGenerationSettings {
  return {
    ...defaultBoardSettings,
    ...project.uiState?.boardSettings
  };
}

function reviewScopeForProject(project: FilmProject): ExportScope {
  return isExportScope(project.uiState?.reviewScope) ? project.uiState.reviewScope : "current";
}

function reviewCategoriesForProject(project: FilmProject): EvoLinkCategory[] {
  const categories = project.uiState?.reviewCategories;
  if (!Array.isArray(categories) || !categories.length) return defaultCategories;
  return categories.filter((category): category is EvoLinkCategory => defaultCategories.includes(category as EvoLinkCategory));
}

function reviewGateStateForProject(project: FilmProject): ReviewGateState {
  const gateState = project.uiState?.reviewGateState;
  return {
    ...defaultReviewGateState,
    ...(gateState && typeof gateState === "object" ? gateState : {})
  };
}

function missingInputText(item: MissingInput): string {
  return `${item.label} ${item.kind} ${item.characterName || ""} ${item.sceneId || ""}`.toLowerCase();
}

function isStoryboardMissingInput(item: MissingInput): boolean {
  const text = missingInputText(item);
  return item.fixRoute === "generate_boards" || text.includes("storyboard") || text.includes("board") || text.includes("frame");
}

function findCharacterForMissingInput(sourceProject: FilmProject, item: MissingInput) {
  const text = missingInputText(item);
  const hintedName = item.characterName?.toLowerCase();
  return sourceProject.characters.find((character) => {
    const name = character.name.toLowerCase();
    return Boolean(
      (item.focusId && character.id === item.focusId)
      || (hintedName && name === hintedName)
      || text.includes(name)
    );
  });
}

function findSceneForMissingInput(sourceProject: FilmProject, item: MissingInput) {
  const text = missingInputText(item);
  return sourceProject.scenes.find((scene) => {
    const paddedNumber = String(scene.number).padStart(2, "0");
    const slugline = scene.slugline.toLowerCase();
    const location = scene.location.toLowerCase();
    return (
      scene.id === item.focusId ||
      scene.id === item.sceneId ||
      `scene_${scene.number}` === item.sceneId ||
      `scene_${paddedNumber}` === item.sceneId ||
      (slugline.length > 2 && text.includes(slugline)) ||
      (location.length > 2 && text.includes(location)) ||
      text.includes(`scene ${scene.number}`) ||
      text.includes(`scene ${paddedNumber}`)
    );
  });
}

function sourceSectionForMissingInput(item: MissingInput): SourceWorkspaceSection | null {
  if (item.fixRoute === "characters") return "characters";
  if (item.fixRoute === "voices") return "voices";
  if (item.fixRoute === "references") return "references";
  const text = missingInputText(item);
  if (text.includes("voice") || text.includes("audio") || text.includes("dialogue") || text.includes("line") || text.includes("speaker")) {
    return "voices";
  }
  if (text.includes("character") || text.includes("card") || text.includes("likeness") || text.includes("wardrobe") || text.includes("cast")) {
    return "characters";
  }
  if (text.includes("reference") || text.includes("location") || text.includes("scene") || text.includes("background") || text.includes("world") || text.includes("set")) {
    return "references";
  }
  return null;
}

function assetBucketForMissingInput(item: MissingInput): AssetBucket | null {
  const text = missingInputText(item);
  if (text.includes("music") || text.includes("score")) return "music";
  if (text.includes("sfx") || text.includes("sound") || text.includes("ambience") || text.includes("foley")) return "sfx";
  if (text.includes("voice") || text.includes("audio") || text.includes("dialogue") || text.includes("line")) return "voiceAudio";
  if (text.includes("character") || text.includes("card") || text.includes("likeness") || text.includes("wardrobe")) return "characterCards";
  if (text.includes("reference") || text.includes("location") || text.includes("scene") || text.includes("background")) return "sceneReferences";
  return null;
}

function workspaceSectionForProject(project: FilmProject, allowProjects = true): WorkspaceSection {
  const section = project.uiState?.activeSection;
  if (!isWorkspaceSection(section)) return "storyboards";
  if (!allowProjects && section === "projects") return "storyboards";
  return section;
}

function uiStatesMatch(left?: ProjectUiState, right?: ProjectUiState): boolean {
  return JSON.stringify(left || {}) === JSON.stringify(right || {});
}

function createBlankProject(): FilmProject {
  const sceneId = "scene-01";
  return normalizeProjectPayload({
    id: generateProjectId("film"),
    title: "Untitled Film",
    goal: "trailer",
    genre: "drama",
    runtimeSeconds: 60,
    aspectRatio: "16:9",
    resolution: "480p",
    stylePreset: "Cinematic Realism",
    colorGrade: "Natural cinematic",
    scriptExcerpt: "",
    scriptUpload: undefined,
    apiPlan: undefined,
    lastStoryboard: undefined,
    lastProductionPackage: undefined,
    uploadedAssets: emptyAssetManifest(),
    generationJobs: [],
    scenes: [
      {
        id: sceneId,
        number: 1,
        slugline: "SCENE 1",
        page: 1,
        durationSeconds: 30,
        synopsis: "Upload a script or add notes to build this scene.",
        characters: [],
        location: "Unassigned",
        readiness: "needs_character",
        frames: [
          {
            id: `${sceneId}-frame-01`,
            sceneId,
            label: "Opening board",
            shotType: "WS · Wide Shot",
            durationSeconds: 5,
            imageUrl: "/assets/storyboards/frame-room.svg",
            source: "internal",
            locked: false,
            queuedRegenerate: false,
            disabled: false,
            generationStatus: "prompt_ready",
            prompt: "Opening storyboard placeholder, ready for script analysis"
          }
        ]
      }
    ],
    characters: [],
    agentBeats: [],
    uiState: {
      activeSection: "storyboards",
      reviewScope: "current",
      reviewCategories: defaultCategories,
      reviewGateState: defaultReviewGateState,
      boardSettings: defaultBoardSettings
    }
  });
}

function boardSourceLabel(source: BoardSource): string {
  if (source === "chatgpt_image") return "ChatGPT Image";
  if (source === "internal_svg") return "Internal SVG";
  return "Uploaded / locked boards";
}

function boardGenerationSuccessStatus(source: BoardSource): StoryboardFrame["generationStatus"] {
  return source === "internal_svg" || source === "none" ? "saved" : "generated";
}

function eligibleFramesForGeneration(
  project: FilmProject,
  selectedSceneId: string,
  settings: BoardGenerationSettings,
  retryFrameIds?: string[]
): StoryboardFrame[] {
  if (settings.source === "none") return [];
  const retryIds = retryFrameIds?.length ? new Set(retryFrameIds) : null;
  const scopedScenes = scenesForScope(project, selectedSceneId, settings.scope);
  const scenesById = new Map(scopedScenes.map((scene) => [scene.id, scene]));
  const scopedFrames = framesForScope(project, selectedSceneId, settings.scope).filter((frame) => {
    if (retryIds && !retryIds.has(frame.id)) return false;
    const scene = scenesById.get(frame.sceneId);
    if (scene && frameBlockedByDisabledChunk(scene, frame)) return false;
    return !frame.disabled && !frame.locked && frame.source !== "uploaded";
  });
  if (settings.source !== "chatgpt_image") return scopedFrames;
  // Explicitly requested frames (retries, scene fills) are generated exactly as asked.
  if (retryIds) return scopedFrames;
  // Single-scene jobs fill every open slot; the one-board-per-scene cap only guards
  // broad multi-scene runs from cost explosions.
  if (settings.scope === "current") {
    const open = scopedFrames.filter((frame) => frame.generationStatus !== "generated");
    return open.length ? open : scopedFrames;
  }
  return scopedScenes
    .map((scene) => {
      const sceneFrames = scopedFrames.filter((frame) => frame.sceneId === scene.id);
      return sceneFrames.find((frame) => frame.generationStatus !== "generated") || sceneFrames[0];
    })
    .filter((frame): frame is StoryboardFrame => Boolean(frame));
}

/** Grow a scene to the requested shot count with open prompt-ready slots; returns the
 *  updated project and the ids of every slot that should generate. */
function expandScenePanels(current: FilmProject, sceneId: string, panels: number, includeGenerated = false): { project: FilmProject; frameIds: string[] } {
  const scene = current.scenes.find((item) => item.id === sceneId);
  if (!scene) return { project: current, frameIds: [] };
  const wanted = Math.max(1, Math.min(12, Math.round(panels)));
  const existing = scene.frames.filter((frame) => !frame.disabled);
  const shotTypes = ["Wide establishing", "Medium", "Close-up", "Insert", "Over-the-shoulder", "Reaction", "Detail", "Wide reverse", "Tracking", "Low angle", "High angle", "Two-shot"];
  const additions: StoryboardFrame[] = [];
  for (let index = existing.length; index < wanted; index += 1) {
    additions.push({
      id: `${scene.id}-frame-${String(index + 1).padStart(2, "0")}-${Date.now().toString(36)}${index}`,
      sceneId: scene.id,
      label: scene.slugline,
      shotType: shotTypes[index % shotTypes.length],
      durationSeconds: Math.max(2, Math.round(scene.durationSeconds / wanted)),
      imageUrl: "",
      source: "internal",
      locked: false,
      queuedRegenerate: false,
      disabled: false,
      generationStatus: "prompt_ready"
    });
  }
  const nextScene = additions.length ? { ...scene, frames: [...scene.frames, ...additions] } : scene;
  const nextProject = additions.length
    ? { ...current, scenes: current.scenes.map((item) => (item.id === sceneId ? nextScene : item)) }
    : current;
  const frameIds = nextScene.frames
    .filter((frame) => !frame.disabled && !frame.locked && frame.source !== "uploaded" && (includeGenerated || frame.generationStatus !== "generated"))
    .map((frame) => frame.id);
  return { project: nextProject, frameIds };
}

function referencePromptNotesForScene(project: FilmProject, sceneId: string): string {
  const references = (project.uploadedAssets?.sceneReferences || []).filter((asset) => (
    asset.sceneId === sceneId && !asset.disabled && asset.parsedReference
  ));
  if (!references.length) return "";
  const notes = references.flatMap((asset) => {
    const parsed = asset.parsedReference;
    if (!parsed) return [];
    const sections = [
      parsed.storyWorld ? `world: ${parsed.storyWorld}` : "",
      parsed.location ? `location: ${parsed.location}` : "",
      parsed.timePeriod ? `time period: ${parsed.timePeriod}` : "",
      parsed.designNotes.length ? `design: ${parsed.designNotes.slice(0, 3).join("; ")}` : "",
      parsed.cameraLens.length ? `camera/lens: ${parsed.cameraLens.slice(0, 3).join("; ")}` : "",
      parsed.lightingAtmosphere.length ? `lighting: ${parsed.lightingAtmosphere.slice(0, 3).join("; ")}` : "",
      parsed.colorPalette.length ? `palette: ${parsed.colorPalette.slice(0, 4).join(", ")}` : "",
      parsed.promptNotes.length ? `prompt notes: ${parsed.promptNotes.slice(0, 4).join("; ")}` : ""
    ].filter(Boolean);
    return sections.length ? [`${asset.label}: ${sections.join(". ")}`] : [];
  });
  return notes.slice(0, 3).join("\n");
}

function frameTargetsForGeneration(project: FilmProject, frames: StoryboardFrame[]): Record<string, unknown>[] {
  const scenesById = new Map(project.scenes.map((scene) => [scene.id, scene]));
  return frames.map((frame, index) => {
    const scene = scenesById.get(frame.sceneId);
    const referenceNotes = referencePromptNotesForScene(project, frame.sceneId);
    const chunk = scene ? chunkForFrame(scene, frame) : undefined;
    const blockedByChunk = Boolean(scene && frameBlockedByDisabledChunk(scene, frame));
    return {
      frameId: frame.id,
      sceneId: frame.sceneId,
      sceneLabel: scene?.slugline || frame.label,
      sceneNumber: scene?.number || index + 1,
      index: index + 1,
      label: frame.label,
      shotType: frame.shotType,
      durationSeconds: frame.durationSeconds,
      prompt: [frame.prompt || "", referenceNotes ? `Reference board notes:\n${referenceNotes}` : ""].filter(Boolean).join("\n\n"),
      referenceNotes,
      chunkId: chunk?.id || "",
      chunkLabel: chunk?.label || "",
      chunkEnabled: chunk ? chunk.enabled : true,
      existingSource: frame.source,
      existingStatus: frame.generationStatus || "",
      sourceMode: frame.source === "uploaded" ? "upload" : "generate",
      sourceOfTruth: frame.source === "uploaded",
      locked: frame.locked,
      disabled: frame.disabled || blockedByChunk,
      include: !frame.disabled && !blockedByChunk,
      sourceUrl: frame.sourceUrl || frame.imageUrl
    };
  });
}

function createBoardGenerationJob(
  project: FilmProject,
  selectedSceneId: string,
  settings: BoardGenerationSettings,
  frameIds: string[],
  skippedCount: number,
  status: BoardGenerationJob["status"] = "queued"
): BoardGenerationJob {
  const timestamp = new Date().toISOString();
  return {
    id: generateProjectId("job"),
    status,
    source: settings.source,
    scope: settings.scope,
    stylePreset: settings.stylePreset,
    panelsPerScene: settings.panelsPerScene,
    sceneIds: scenesForScope(project, selectedSceneId, settings.scope).map((scene) => scene.id),
    frameIds,
    createdAt: timestamp,
    updatedAt: timestamp,
    completedAt: status === "generated" || status === "failed" ? timestamp : undefined,
    generatedCount: 0,
    skippedCount,
    error: undefined
  };
}

function withGenerationJob(project: FilmProject, job: BoardGenerationJob): FilmProject {
  return {
    ...project,
    generationJobs: [job, ...(project.generationJobs || [])].slice(0, 40)
  };
}

function withGenerationJobPatch(project: FilmProject, jobId: string, patch: Partial<BoardGenerationJob>): FilmProject {
  const timestamp = new Date().toISOString();
  return {
    ...project,
    generationJobs: (project.generationJobs || []).map((job) => (
      job.id === jobId ? { ...job, ...patch, updatedAt: timestamp } : job
    ))
  };
}

function withFrameGenerationStatus(
  project: FilmProject,
  frameIds: string[],
  status: StoryboardFrame["generationStatus"],
  jobId: string,
  options: { error?: string; timestamp?: string } = {}
): FilmProject {
  const ids = new Set(frameIds);
  const timestamp = options.timestamp || new Date().toISOString();
  return {
    ...project,
    scenes: project.scenes.map((scene) => ({
      ...scene,
      frames: scene.frames.map((frame) => {
        if (!ids.has(frame.id)) {
          if (frame.source === "uploaded" && frame.generationStatus !== "uploaded") {
            return { ...frame, generationStatus: "uploaded", queuedRegenerate: false, generationError: undefined };
          }
          return frame;
        }
        if (frame.source === "uploaded") {
          return { ...frame, generationStatus: "uploaded", generationJobId: jobId, queuedRegenerate: false, generationError: undefined };
        }
        if (frame.locked || frame.disabled) return frame;
        return {
          ...frame,
          generationStatus: status,
          generationJobId: jobId,
          generationError: options.error || undefined,
          generatedAt: status === "generated" || status === "saved" ? timestamp : frame.generatedAt,
          queuedRegenerate: status === "queued" || status === "failed"
        };
      })
    }))
  };
}

async function copyTextToClipboard(text: string): Promise<void> {
  let clipboardError: unknown;
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (error) {
      clipboardError = error;
    }
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);
  if (!copied) {
    throw clipboardError instanceof Error ? clipboardError : new Error("Clipboard permission denied.");
  }
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

function dataUrlToBase64(dataUrl: string): string {
  return dataUrl.includes(",") ? dataUrl.split(",").slice(1).join(",") : dataUrl;
}

async function readScriptUpload(file: File): Promise<ProjectScriptUpload> {
  const uploadedAt = new Date().toISOString();
  const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
  if (isPdf) {
    const dataUrl = await readFileAsDataUrl(file);
    return {
      fileName: file.name,
      mimeType: file.type || "application/pdf",
      size: file.size,
      dataBase64: dataUrlToBase64(dataUrl),
      uploadedAt
    };
  }
  return {
    fileName: file.name,
    mimeType: file.type || "text/plain",
    size: file.size,
    text: await readFileAsText(file),
    uploadedAt
  };
}

export default function App() {
  const [project, setProject] = useState<FilmProject>(() => loadProject());
  const [theme, setTheme] = useState<ThemeMode>(() => loadTheme());
  const [accessKey] = useState(() => loadAccessKey());
  const [restoreRequest] = useState<RestoreRequest | null>(() => loadRestoreRequest(accessKey));
  const [selectedSceneId, setSelectedSceneId] = useState(() => activeSceneIdForProject(project, project.uiState?.activeSceneId));
  const [selectedFrameId, setSelectedFrameId] = useState(() => activeFrameIdForProject(project, selectedSceneId, project.uiState?.selectedFrameId));
  const [boardSettings, setBoardSettings] = useState<BoardGenerationSettings>(() => boardSettingsForProject(project));
  const [showGenerate, setShowGenerate] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [showPromptPreview, setShowPromptPreview] = useState(false);
  const [showAssetLibrary, setShowAssetLibrary] = useState(false);
  const [reviewScope, setReviewScope] = useState<ExportScope>(() => reviewScopeForProject(project));
  const [reviewCategories, setReviewCategories] = useState<EvoLinkCategory[]>(() => reviewCategoriesForProject(project));
  const [reviewGateState, setReviewGateState] = useState<ReviewGateState>(() => reviewGateStateForProject(project));
  const [apiStatus, setApiStatus] = useState<ApiStatus>("checking");
  const [engineStale, setEngineStale] = useState(false);
  const [cloudStorageReady, setCloudStorageReady] = useState(false);
  const [cloudRestoreAttempted, setCloudRestoreAttempted] = useState(() => !restoreRequest);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>(() => (restoreRequest ? "Restoring" : "Local only"));
  const [autosaveRetryNonce, setAutosaveRetryNonce] = useState(0);
  const [cloudVersion, setCloudVersion] = useState("");
  const [draftConflict, setDraftConflict] = useState<CloudDraftConflict | null>(null);
  const [workflowView, setWorkflowView] = useState<WorkflowView>("workspace");
  const [activeSection, setActiveSection] = useState<WorkspaceSection>(() => workspaceSectionForProject(project));
  const [sourceFocus, setSourceFocus] = useState<SourceFocusTarget | null>(null);
  const [importInitialBucket, setImportInitialBucket] = useState<AssetBucket>("characterCards");
  const [pendingAnalysis, setPendingAnalysis] = useState<Record<string, unknown> | null>(null);
  const [pendingProject, setPendingProject] = useState<FilmProject | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isGeneratingBoards, setIsGeneratingBoards] = useState(false);
  const [isEasyModeRunning, setIsEasyModeRunning] = useState(false);
  const [isMakingPoster, setIsMakingPoster] = useState(false);
  const [easyModeOptions, setEasyModeOptions] = useState<EasyModeOptions>(defaultEasyModeOptions);
  const [easyModeProgress, setEasyModeProgress] = useState<EasyModeProgress | null>(null);
  // "simple" is the new Apple-grade flow; "pro" is the full workspace. Persisted per browser.
  const [experience, setExperienceState] = useState<"simple" | "pro">(() => {
    const stored = window.localStorage.getItem(EXPERIENCE_KEY);
    // New visitors land in the Apple-grade flow; Pro stays one click away (and persists once chosen).
    return stored === "simple" || stored === "pro" ? stored : "simple";
  });
  // ?classic=1 renders the original Pro workspace — the escape hatch while Pro Glass beds in.
  const [proClassic] = useState<boolean>(() => {
    try {
      return new URLSearchParams(window.location.search).has("classic");
    } catch {
      return false;
    }
  });

  function setExperience(next: "simple" | "pro") {
    window.localStorage.setItem(EXPERIENCE_KEY, next);
    setExperienceState(next);
  }
  const [isSavingPackage, setIsSavingPackage] = useState(false);
  const [parsingReferenceId, setParsingReferenceId] = useState("");
  const [cloudProjects, setCloudProjects] = useState<CloudProjectSummary[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [projectLibraryMessage, setProjectLibraryMessage] = useState("");
  const [notice, setNotice] = useState("React migration started. Autosave is active.");
  const autosaveRequestRef = useRef(0);
  const lastSavedProjectJsonRef = useRef("");
  const assetUploadRetryRef = useRef(new Map<string, AssetUploadRetryRecord>());
  const uploadSync = useMemo(() => uploadSyncSummaryForProject(project), [project]);

  function applyProjectWorkspaceState(nextProject: FilmProject, options: { allowProjects?: boolean; forceSection?: WorkspaceSection } = {}) {
    const sceneId = activeSceneIdForProject(nextProject, nextProject.uiState?.activeSceneId);
    const frameId = activeFrameIdForProject(nextProject, sceneId, nextProject.uiState?.selectedFrameId);
    setSelectedSceneId(sceneId);
    setSelectedFrameId(frameId);
    setBoardSettings(boardSettingsForProject(nextProject));
    setReviewScope(reviewScopeForProject(nextProject));
    setReviewCategories(reviewCategoriesForProject(nextProject));
    setReviewGateState(reviewGateStateForProject(nextProject));
    setActiveSection(options.forceSection || workspaceSectionForProject(nextProject, options.allowProjects ?? true));
    // Scene scope is project-specific; progress belongs to the previous run.
    setEasyModeOptions((current) => ({ ...current, sceneScope: "all" }));
    setEasyModeProgress(null);
    setSourceFocus(null);
    setShowGenerate(false);
    setShowReview(false);
    setShowPromptPreview(false);
    setShowAssetLibrary(false);
    setWorkflowView("workspace");
  }

  function switchToProject(nextProject: FilmProject, options: { allowProjects?: boolean; forceSection?: WorkspaceSection } = {}) {
    const normalized = normalizeProjectPayload(nextProject);
    setProject(normalized);
    applyProjectWorkspaceState(normalized, options);
  }

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(project));
  }, [project]);

  useEffect(() => {
    const nextUiState: ProjectUiState = {
      activeSceneId: selectedSceneId,
      selectedFrameId,
      activeSection,
      reviewScope,
      reviewCategories,
      reviewGateState,
      boardSettings
    };
    setProject((current) => (
      uiStatesMatch(current.uiState, nextUiState) ? current : { ...current, uiState: nextUiState }
    ));
  }, [activeSection, boardSettings, reviewCategories, reviewGateState, reviewScope, selectedFrameId, selectedSceneId]);

  useEffect(() => {
    let cancelled = false;
    getApiHealth()
      .then((health) => {
        setEngineStale(Boolean(health.ok) && Number(health.featureLevel || 0) < EXPECTED_ENGINE_FEATURE_LEVEL);
        if (cancelled) return;
        const projectsReady = Boolean(health.ok && health.storage?.projects);
        setApiStatus(health.ok ? "online" : "offline");
        setCloudStorageReady(projectsReady);
        setSaveStatus(projectsReady ? (restoreRequest ? "Restoring" : "Saving") : "Local only");
        setNotice(
          projectsReady
            ? restoreRequest
              ? "API connected. Restoring saved project..."
              : "API connected. Project autosave is active."
            : health.ok
              ? "API connected, but cloud project storage is not configured. Browser autosave is active."
              : "Working locally. Browser autosave is active."
        );
      })
      .catch(() => {
        if (!cancelled) {
          setApiStatus("offline");
          setCloudStorageReady(false);
          setSaveStatus("Local only");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [restoreRequest]);

  useEffect(() => {
    if (apiStatus !== "online" || !cloudStorageReady) {
      setSaveStatus("Local only");
      return;
    }
    if (!cloudRestoreAttempted) {
      setSaveStatus("Restoring");
      return;
    }
    if (draftConflict) {
      setSaveStatus("Restore draft");
      return;
    }
    const projectJson = JSON.stringify(project);
    if (lastSavedProjectJsonRef.current === projectJson) {
      setSaveStatus("Saved");
      return;
    }
    const requestId = autosaveRequestRef.current + 1;
    autosaveRequestRef.current = requestId;
    setSaveStatus("Saving");
    const timeout = window.setTimeout(() => {
      saveCloudProject(project, WORKSPACE_ID, accessKey, cloudVersion)
        .then((response) => {
          if (autosaveRequestRef.current !== requestId) return;
          if (response.ok === false) {
            if (response.status === "access_denied") {
              const forkedProject = normalizeProjectPayload({
                ...project,
                id: generateProjectId("film"),
                uiState: {
                  ...project.uiState,
                  activeSection
                }
              });
              setCloudVersion("");
              setDraftConflict(null);
              lastSavedProjectJsonRef.current = "";
              setProject(forkedProject);
              clearProjectRestoreUrl();
              setSaveStatus("Saving");
              setNotice("Created a private browser-owned draft so autosave can continue without overwriting a protected project.");
              return;
            }
            throw new Error(response.message || "Cloud save failed.");
          }
          const savedAt = cloudUpdatedAtFromResponse(response);
          if (savedAt) setCloudVersion(savedAt);
          lastSavedProjectJsonRef.current = projectJson;
          setSaveStatus("Saved");
        })
        .catch((error) => {
          if (autosaveRequestRef.current !== requestId) return;
          const conflict = cloudDraftConflictFromError(error);
          if (conflict) {
            setDraftConflict(conflict);
            setSaveStatus("Restore draft");
            setNotice(`${conflict.message} Choose which draft should continue.`);
            return;
          }
          setSaveStatus("Save failed");
          setNotice(`Cloud autosave failed: ${error instanceof Error ? error.message : String(error)} Browser autosave is still active. Retry cloud save when ready.`);
        });
    }, 700);
    return () => window.clearTimeout(timeout);
  }, [accessKey, activeSection, apiStatus, autosaveRetryNonce, cloudRestoreAttempted, cloudStorageReady, cloudVersion, draftConflict, project]);

  useEffect(() => {
    if (!restoreRequest || cloudRestoreAttempted || apiStatus !== "online" || !cloudStorageReady) return;
    let cancelled = false;
    setSaveStatus("Restoring");
    setNotice(`Restoring project ${restoreRequest.projectId} from cloud storage...`);
    loadCloudProject(restoreRequest.projectId, restoreRequest.accessKey)
      .then((response) => {
        if (cancelled) return;
        if (response.ok === false || !response.project) {
          throw new Error(response.message || "Saved project was unavailable.");
        }
        const restoredProject = normalizeProjectPayload(response.project);
        const restoredAt = cloudUpdatedAtFromResponse(response);
        if (restoredAt) setCloudVersion(restoredAt);
        setDraftConflict(null);
        switchToProject(restoredProject);
        setCloudRestoreAttempted(true);
        setSaveStatus("Saved");
        lastSavedProjectJsonRef.current = JSON.stringify(restoredProject);
        setNotice(`${restoredProject.title} restored. Edits now autosave on this device.`);
      })
      .catch((error) => {
        if (cancelled) return;
        setCloudRestoreAttempted(true);
        setSaveStatus("Save failed");
        setNotice(`Cloud restore failed: ${error instanceof Error ? error.message : String(error)} Browser autosave is still available.`);
      });
    return () => {
      cancelled = true;
    };
  }, [apiStatus, cloudRestoreAttempted, cloudStorageReady, restoreRequest]);

  useEffect(() => {
    window.localStorage.setItem(THEME_KEY, theme);
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  useEffect(() => {
    if (!showGenerate) return;
    requestAnimationFrame(() => {
      const behavior = window.matchMedia("(prefers-reduced-motion: reduce), (max-width: 720px)").matches ? "auto" : "smooth";
      document.querySelector(".generation-sheet")?.scrollIntoView({ behavior, block: "start" });
    });
  }, [showGenerate]);

  useEffect(() => {
    if (!showReview) return;
    requestAnimationFrame(() => {
      const behavior = window.matchMedia("(prefers-reduced-motion: reduce), (max-width: 720px)").matches ? "auto" : "smooth";
      document.querySelector(".review-panel")?.scrollIntoView({ behavior, block: "start" });
    });
  }, [showReview]);

  useEffect(() => {
    if (activeSection !== "projects") return;
    void refreshCloudProjects();
  }, [activeSection, apiStatus, cloudStorageReady]);

  const activeScene = useMemo(
    () => project.scenes.find((scene) => scene.id === selectedSceneId) || project.scenes[0],
    [project.scenes, selectedSceneId]
  );

  const selectedFrame = useMemo(() => {
    return activeScene?.frames.find((frame) => frame.id === selectedFrameId) || activeScene?.frames[0];
  }, [activeScene, selectedFrameId]);

  async function refreshCloudProjects() {
    if (!cloudStorageReady) {
      setCloudProjects([]);
      setProjectLibraryMessage(
        apiStatus === "checking"
          ? "Checking project storage..."
          : "Cloud project storage is not available. Browser autosave is still protecting the current draft."
      );
      return;
    }
    setIsLoadingProjects(true);
    setProjectLibraryMessage("Loading saved drafts...");
    try {
      const response = await listCloudProjects(WORKSPACE_ID, accessKey);
      if (response.ok === false) throw new Error(response.message || "Project list unavailable.");
      setCloudProjects(response.projects || []);
      setProjectLibraryMessage(response.projects?.length ? "Saved drafts loaded." : "No cloud drafts yet.");
    } catch (error) {
      setProjectLibraryMessage(`Project list failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsLoadingProjects(false);
    }
  }

  async function loadProjectForAction(projectId: string): Promise<FilmProject> {
    if (projectId === project.id) return project;
    const response = await loadCloudProject(projectId, accessKey);
    if (response.ok === false || !response.project) {
      throw new Error(response.message || "Saved project was unavailable.");
    }
    const loadedAt = cloudUpdatedAtFromResponse(response);
    if (loadedAt) setCloudVersion(loadedAt);
    setDraftConflict(null);
    return normalizeProjectPayload(response.project);
  }

  async function handleOpenSavedProject(projectId: string) {
    setNotice("Opening saved project...");
    try {
      const nextProject = await loadProjectForAction(projectId);
      switchToProject(nextProject, { allowProjects: false });
      lastSavedProjectJsonRef.current = JSON.stringify(nextProject);
      window.history.replaceState(null, "", buildProjectRestoreUrl(nextProject.id, accessKey));
      setNotice(`${nextProject.title} opened. Workspace restored.`);
    } catch (error) {
      setNotice(`Open project failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async function handleReviewProjectPackage(projectId: string) {
    setNotice(projectId === project.id ? "Opening package history..." : "Opening saved project package...");
    try {
      const nextProject = await loadProjectForAction(projectId);
      switchToProject(nextProject, { allowProjects: false, forceSection: "storyboards" });
      lastSavedProjectJsonRef.current = JSON.stringify(nextProject);
      setShowReview(true);
      window.history.replaceState(null, "", buildProjectRestoreUrl(nextProject.id, accessKey));
      setNotice(`${nextProject.title} package history opened.`);
    } catch (error) {
      setNotice(`Open package failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async function handleDuplicateProject(projectId: string) {
    setNotice("Duplicating project...");
    try {
      const sourceProject = await loadProjectForAction(projectId);
      const duplicated = normalizeProjectPayload({
        ...sourceProject,
        id: generateProjectId("film-copy"),
        title: `${sourceProject.title || "Untitled Film"} Copy`,
        uiState: {
          ...sourceProject.uiState,
          activeSection: "storyboards"
        }
      });
      const response = await saveCloudProject(duplicated, WORKSPACE_ID, accessKey);
      if (response.ok === false) throw new Error(response.message || "Duplicate save failed.");
      const savedAt = cloudUpdatedAtFromResponse(response);
      if (savedAt) setCloudVersion(savedAt);
      setDraftConflict(null);
      switchToProject(duplicated, { forceSection: "storyboards" });
      lastSavedProjectJsonRef.current = JSON.stringify(duplicated);
      window.history.replaceState(null, "", buildProjectRestoreUrl(duplicated.id, accessKey));
      setNotice(`${duplicated.title} duplicated and opened.`);
      void refreshCloudProjects();
    } catch (error) {
      setNotice(`Duplicate failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async function handleDeleteProject(projectId: string) {
    setNotice("Deleting saved project...");
    try {
      const response = await deleteCloudProject(projectId, accessKey);
      if (response.ok === false) throw new Error(response.message || "Delete failed.");
      setNotice("Saved project deleted.");
      if (projectId === project.id) {
        const blankProject = createBlankProject();
        setCloudVersion("");
        setDraftConflict(null);
        lastSavedProjectJsonRef.current = "";
        switchToProject(blankProject, { forceSection: "storyboards" });
        setWorkflowView("intake");
        clearProjectRestoreUrl();
      }
      void refreshCloudProjects();
    } catch (error) {
      setNotice(`Delete failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  function handleNewProject() {
    const blankProject = createBlankProject();
    setCloudVersion("");
    setDraftConflict(null);
    lastSavedProjectJsonRef.current = "";
    switchToProject(blankProject, { forceSection: "storyboards" });
    setWorkflowView("intake");
    clearProjectRestoreUrl();
    setNotice("New project created. Add basics, script, and source assets.");
  }

  /** Start a film from stills alone — no script. The images become scene-reference
      plates; the Objective Cuts assistant drafts the whole cut from them + direction. */
  const pendingImageFilmRef = useRef<{ projectId: string; files: File[] } | null>(null);

  function handleNewFromImages(files: File[]) {
    if (!files.length) return;
    const imageProject = { ...createBlankProject(), title: "Image Film" };
    pendingImageFilmRef.current = { projectId: imageProject.id, files };
    setCloudVersion("");
    setDraftConflict(null);
    lastSavedProjectJsonRef.current = "";
    switchToProject(imageProject, { forceSection: "storyboards" });
    clearProjectRestoreUrl();
    setNotice(`Image film started — saving ${files.length} stills as your plates…`);
  }

  useEffect(() => {
    const pending = pendingImageFilmRef.current;
    if (!pending || project.id !== pending.projectId) return;
    pendingImageFilmRef.current = null;
    handleAssetUpload("sceneReferences", pending.files, { kind: "scene_reference" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  function handleRestoreCloudDraft() {
    if (!draftConflict) return;
    const restoredProject = normalizeProjectPayload(draftConflict.project);
    setCloudVersion(draftConflict.savedAt);
    setDraftConflict(null);
    switchToProject(restoredProject, { allowProjects: false });
    lastSavedProjectJsonRef.current = JSON.stringify(restoredProject);
    window.history.replaceState(null, "", buildProjectRestoreUrl(restoredProject.id, accessKey));
    setSaveStatus("Saved");
    setNotice(`${restoredProject.title} restored from cloud. Local browser autosave now matches the saved draft.`);
  }

  function handleKeepLocalDraft() {
    if (!draftConflict) return;
    setCloudVersion(draftConflict.savedAt);
    setDraftConflict(null);
    lastSavedProjectJsonRef.current = "";
    setSaveStatus("Saving");
    setNotice("Keeping your browser draft. The next autosave will update the cloud copy.");
  }

  function handleRetryCloudAutosave() {
    if (apiStatus !== "online" || !cloudStorageReady) {
      setSaveStatus("Local only");
      setNotice("Cloud storage is unavailable. Browser autosave is still protecting this draft.");
      return;
    }
    lastSavedProjectJsonRef.current = "";
    setSaveStatus("Saving");
    setNotice("Retrying cloud autosave. Browser autosave remains active until the cloud save finishes.");
    setAutosaveRetryNonce((value) => value + 1);
  }

  function handleKeepBrowserDraftAfterSaveFailure() {
    setSaveStatus("Local only");
    setNotice("Continuing with browser autosave. Press Retry Cloud Save when you want to sync this draft again.");
  }

  function updateFrame(frameId: string, updater: (frame: StoryboardFrame) => StoryboardFrame) {
    setProject((current) => ({
      ...current,
      scenes: current.scenes.map((scene) => ({
        ...scene,
        frames: scene.frames.map((frame) => (frame.id === frameId ? updater(frame) : frame))
      }))
    }));
  }

  function handleSaveFramePrompt(prompt: string, queueRegenerate = false) {
    if (!selectedFrame) return;
    const cleanPrompt = prompt.trim();
    updateFrame(selectedFrame.id, (frame) => {
      const shouldQueue = Boolean(queueRegenerate && !frame.locked && !frame.disabled);
      return {
        ...frame,
        prompt: cleanPrompt,
        queuedRegenerate: shouldQueue,
        generationStatus: shouldQueue ? "queued" : frame.source === "internal" ? "prompt_ready" : frame.generationStatus,
        generationError: queueRegenerate ? undefined : frame.generationError
      };
    });
    setShowPromptPreview(false);
    setNotice(
      queueRegenerate
        ? selectedFrame.locked || selectedFrame.disabled
          ? "Prompt saved. Locked or disabled boards are not queued for regeneration."
          : "Prompt saved and board queued for regeneration."
        : "Frame prompt saved for the EvoLink dry-run package."
    );
  }

  function handleUseLibraryFrame(sourceFrame: StoryboardFrame) {
    if (!selectedFrame) return;
    if (sourceFrame.id === selectedFrame.id) {
      setNotice("That board is already selected.");
      return;
    }
    updateFrame(selectedFrame.id, (frame) => ({
      ...frame,
      imageUrl: sourceFrame.imageUrl,
      sourceUrl: sourceFrame.sourceUrl || sourceFrame.imageUrl,
      storageKey: sourceFrame.storageKey,
      cloudStatus: sourceFrame.cloudStatus,
      cloudMessage: sourceFrame.cloudMessage,
      source: sourceFrame.source,
      fileName: sourceFrame.fileName || sourceFrame.label,
      prompt: sourceFrame.prompt || frame.prompt,
      generationStatus: sourceFrame.generationStatus || frame.generationStatus,
      generationError: undefined,
      queuedRegenerate: false,
      disabled: false,
      locked: sourceFrame.source === "uploaded" ? true : frame.locked
    }));
    setShowAssetLibrary(false);
    setNotice(`${sourceFrame.label || sourceFrame.shotType} reused on the selected board.`);
  }

  function handleAttachLibraryReference(assetId: string) {
    if (!activeScene) return;
    setProject((current) => {
      const uploadedAssets = current.uploadedAssets || emptyAssetManifest();
      const asset = uploadedAssets.sceneReferences.find((item) => item.assetId === assetId);
      if (!asset) return current;
      return {
        ...current,
        uploadedAssets: {
          ...uploadedAssets,
          sceneReferences: uploadedAssets.sceneReferences.map((item) => (
            item.assetId === assetId ? { ...item, sceneId: activeScene.id, disabled: false } : item
          ))
        },
        scenes: current.scenes.map((scene) => (
          scene.id === activeScene.id && scene.readiness === "missing_reference"
            ? { ...scene, readiness: "ready" }
            : scene
        ))
      };
    });
    setNotice("Scene reference attached to the active scene.");
  }

  function handleDeleteAsset(bucket: AssetBucket, assetId: string) {
    const uploadedAssets = project.uploadedAssets || emptyAssetManifest();
    const record = uploadedAssets[bucket].find((asset) => asset.assetId === assetId);
    if (!record) return;
    const ok = window.confirm(
      `Delete "${record.label || record.fileName}"?\n\nThis removes it from the project and deletes the stored file. ` +
      `Boards or cut plans that reference it will need a new pick.`
    );
    if (!ok) return;
    setProject((current) => {
      const manifest = current.uploadedAssets || emptyAssetManifest();
      return {
        ...current,
        uploadedAssets: {
          ...manifest,
          [bucket]: manifest[bucket].filter((asset) => asset.assetId !== assetId)
        }
      };
    });
    if (record.storageKey) {
      void deleteAssetObject(String(record.storageKey)).catch(() => undefined);
    }
    setNotice(`Deleted ${record.label || record.fileName}.`);
  }

  /** Smart Ingest: dump everything, let the librarian sort it. Mime + heuristics
      route audio/video/docs instantly; images get a cheap vision pass that picks the
      bucket, the character, and a look label — high-confidence calls auto-file. */
  async function handleSmartIngest(files: File[]) {
    if (!files.length) return;
    const audioDuration = (file: File) =>
      new Promise<number>((resolve) => {
        const url = URL.createObjectURL(file);
        const probe = new Audio();
        probe.preload = "metadata";
        probe.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(probe.duration || 0); };
        probe.onerror = () => { URL.revokeObjectURL(url); resolve(0); };
        probe.src = url;
      });
    const images: File[] = [];
    const plan: Array<{ bucket: AssetBucket; file: File; kind: string }> = [];
    for (const file of files) {
      const name = file.name.toLowerCase();
      if (file.type.startsWith("video/") || /\.(mp4|mov|webm|m4v)$/.test(name)) {
        plan.push({ bucket: "videoReferences", file, kind: "video_reference" });
      } else if (/\.(pdf|docx|txt|md)$/.test(name) && !file.type.startsWith("image/")) {
        plan.push({ bucket: "documents", file, kind: "document" });
      } else if (file.type.startsWith("audio/") || /\.(mp3|wav|m4a|aiff|flac)$/.test(name)) {
        const duration = await audioDuration(file);
        const bucket: AssetBucket = /sfx|whoosh|riser|impact|foley|ambien/.test(name)
          ? "sfx"
          : duration >= 75 ? "music" : "voiceAudio";
        plan.push({ bucket, file, kind: bucket === "music" ? "music" : bucket === "sfx" ? "sfx" : "voice_audio" });
      } else if (file.type.startsWith("image/") || /\.(png|jpe?g|webp|gif)$/.test(name)) {
        images.push(file);
      } else {
        plan.push({ bucket: "documents", file, kind: "document" });
      }
    }
    // Non-image files: straight to their buckets.
    const byBucket = new Map<AssetBucket, File[]>();
    for (const item of plan) {
      byBucket.set(item.bucket, [...(byBucket.get(item.bucket) || []), item.file]);
    }
    for (const [bucket, bucketFiles] of byBucket) {
      const kind = plan.find((item) => item.bucket === bucket)?.kind || "";
      handleAssetUpload(bucket, bucketFiles, kind ? { kind } : {});
    }
    if (!images.length) return;
    // Images: land provisionally in scene references, then the librarian files them.
    setNotice(`Smart Ingest: uploading ${images.length} image${images.length === 1 ? "" : "s"}, then filing them…`);
    const localRecords = images.map((file, index) => buildLocalAssetRecord("sceneReferences", file, index, { kind: "scene_reference" }));
    appendUploadedAssetRecords("sceneReferences", localRecords);
    const uploaded = await Promise.all(
      images.map((file, index) => startAssetUpload("sceneReferences", file, localRecords[index], { kind: "scene_reference" }))
    );
    const ingestContext = {
      characters: project.characters.map((character) => character.name).slice(0, 30),
      places: Array.from(new Set(project.scenes.map((scene) => scene.slugline))).slice(0, 30)
    };
    let filed = 0;
    let kept = 0;
    for (const record of uploaded) {
      if (!record.storageKey || !record.assetId) { kept += 1; continue; }
      try {
        const result = await classifyAsset({ storageKey: record.storageKey, fileName: record.fileName, context: ingestContext });
        if (!result.ok) { kept += 1; continue; }
        const confident = (result.confidence || 0) >= 0.65;
        const targetBucket = (result.bucket || "sceneReferences") as AssetBucket;
        setProject((current) => {
          const manifest = current.uploadedAssets || emptyAssetManifest();
          const source = manifest.sceneReferences.find((asset) => asset.assetId === record.assetId);
          if (!source) return current;
          const updated = {
            ...source,
            label: result.lookLabel || source.label,
            characterName: result.characterName || source.characterName
          };
          if (confident && targetBucket !== "sceneReferences") {
            return {
              ...current,
              uploadedAssets: {
                ...manifest,
                sceneReferences: manifest.sceneReferences.filter((asset) => asset.assetId !== record.assetId),
                [targetBucket]: [...manifest[targetBucket], { ...updated, kind: targetBucket === "characterCards" ? "character_card" : targetBucket === "branding" ? "branding" : targetBucket === "styleRefs" ? "style_reference" : updated.kind }]
              }
            };
          }
          return {
            ...current,
            uploadedAssets: {
              ...manifest,
              sceneReferences: manifest.sceneReferences.map((asset) => (asset.assetId === record.assetId ? updated : asset))
            }
          };
        });
        if (confident) filed += 1; else kept += 1;
      } catch {
        kept += 1;
      }
    }
    setNotice(`Smart Ingest done: ${filed} filed automatically, ${kept} left in Scene references for your review.`);
  }

  function handleRenameAsset(bucket: AssetBucket, assetId: string) {
    const uploadedAssets = project.uploadedAssets || emptyAssetManifest();
    const record = uploadedAssets[bucket].find((asset) => asset.assetId === assetId);
    if (!record) return;
    const next = window.prompt("New label for this asset:", record.label || record.fileName);
    if (!next || !next.trim()) return;
    setProject((current) => {
      const manifest = current.uploadedAssets || emptyAssetManifest();
      return {
        ...current,
        uploadedAssets: {
          ...manifest,
          [bucket]: manifest[bucket].map((asset) => (asset.assetId === assetId ? { ...asset, label: next.trim() } : asset))
        }
      };
    });
  }

  function handleDuplicateAsset(bucket: AssetBucket, assetId: string) {
    const uploadedAssets = project.uploadedAssets || emptyAssetManifest();
    const record = uploadedAssets[bucket].find((asset) => asset.assetId === assetId);
    if (!record) return;
    const copy = {
      ...record,
      assetId: `asset_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
      label: `${record.label || record.fileName} (copy)`
    };
    setProject((current) => {
      const manifest = current.uploadedAssets || emptyAssetManifest();
      const index = manifest[bucket].findIndex((asset) => asset.assetId === assetId);
      const next = [...manifest[bucket]];
      next.splice(index + 1, 0, copy);
      return { ...current, uploadedAssets: { ...manifest, [bucket]: next } };
    });
    setNotice(`Duplicated ${record.label || record.fileName} — rename or move the copy as needed.`);
  }

  function handleMoveAsset(bucket: AssetBucket, assetId: string, target: AssetBucket) {
    if (bucket === target) return;
    const uploadedAssets = project.uploadedAssets || emptyAssetManifest();
    const record = uploadedAssets[bucket].find((asset) => asset.assetId === assetId);
    if (!record) return;
    setProject((current) => {
      const manifest = current.uploadedAssets || emptyAssetManifest();
      return {
        ...current,
        uploadedAssets: {
          ...manifest,
          [bucket]: manifest[bucket].filter((asset) => asset.assetId !== assetId),
          [target]: [...manifest[target], record]
        }
      };
    });
    setNotice(`Moved ${record.label || record.fileName} to ${sourceBucketLabels[target]}.`);
  }

  function handleToggleLibraryAssetDisabled(bucket: AssetBucket, assetId: string) {
    let nextDisabled = false;
    setProject((current) => {
      const uploadedAssets = current.uploadedAssets || emptyAssetManifest();
      return {
        ...current,
        uploadedAssets: {
          ...uploadedAssets,
          [bucket]: uploadedAssets[bucket].map((asset) => {
            if (asset.assetId !== assetId) return asset;
            nextDisabled = !asset.disabled;
            return { ...asset, disabled: nextDisabled };
          })
        }
      };
    });
    setNotice(nextDisabled ? "Asset disabled for future provider packages." : "Asset enabled for future provider packages.");
  }

  function handleFrameSourceAssignmentsChange(assignments?: StoryboardFrameSourceAssignments) {
    if (!selectedFrame) return;
    updateFrame(selectedFrame.id, (frame) => ({
      ...frame,
      sourceAssignments: assignments
    }));
    setNotice(assignments ? "Selected board source assignments updated." : "Selected board now uses scene default sources.");
  }

  function updateScene(sceneId: string, updater: (scene: typeof project.scenes[number]) => typeof project.scenes[number]) {
    setProject((current) => ({
      ...current,
      scenes: current.scenes.map((scene) => (scene.id === sceneId ? updater(scene) : scene))
    }));
  }

  function applySceneChunks(sceneId: string, updater: (chunks: SceneChunk[], scene: typeof project.scenes[number]) => SceneChunk[]) {
    updateScene(sceneId, (scene) => {
      const chunks = updater(sceneChunks(scene), scene).map((chunk, index) => ({
        ...chunk,
        sceneId,
        index: index + 1,
        label: chunk.label || `Chunk ${index + 1}`
      }));
      const durationSeconds = chunks.reduce((total, chunk) => total + chunk.durationSeconds, 0);
      return {
        ...scene,
        durationSeconds: Math.max(1, durationSeconds),
        chunks
      };
    });
  }

  function handleSceneDurationChange(sceneId: string, durationSeconds: number) {
    updateScene(sceneId, (scene) => {
      const nextScene = { ...scene, durationSeconds: Math.max(1, Math.round(durationSeconds || scene.durationSeconds)) };
      return { ...nextScene, chunks: buildSceneChunks(nextScene) };
    });
    setNotice("Scene duration updated and chunks rebuilt.");
  }

  function handleResetSceneChunks(sceneId: string) {
    updateScene(sceneId, (scene) => ({ ...scene, chunks: buildSceneChunks(scene) }));
    setNotice("Scene chunks rebuilt from duration and enabled boards.");
  }

  function handleChunkDurationChange(sceneId: string, chunkId: string, durationSeconds: number) {
    applySceneChunks(sceneId, (chunks) =>
      chunks.map((chunk) => (
        chunk.id === chunkId && !chunk.locked
          ? { ...chunk, durationSeconds: Math.max(1, Math.round(durationSeconds || chunk.durationSeconds)) }
          : chunk
      ))
    );
    setNotice("Chunk duration updated.");
  }

  function handleChunkToggle(sceneId: string, chunkId: string, field: "enabled" | "locked") {
    applySceneChunks(sceneId, (chunks) =>
      chunks.map((chunk) => (chunk.id === chunkId ? { ...chunk, [field]: !chunk[field] } : chunk))
    );
    setNotice(field === "enabled" ? "Chunk export state updated." : "Chunk lock updated.");
  }

  function handleSplitChunk(sceneId: string, chunkId: string) {
    applySceneChunks(sceneId, (chunks) => {
      const chunkIndex = chunks.findIndex((chunk) => chunk.id === chunkId);
      if (chunkIndex < 0) return chunks;
      const chunk = chunks[chunkIndex];
      if (chunk.locked || chunk.durationSeconds <= 2) return chunks;
      const firstDuration = Math.max(1, Math.floor(chunk.durationSeconds / 2));
      const secondDuration = Math.max(1, chunk.durationSeconds - firstDuration);
      const splitAt = Math.max(1, Math.ceil(chunk.frameIds.length / 2));
      const first: SceneChunk = {
        ...chunk,
        durationSeconds: firstDuration,
        frameIds: chunk.frameIds.slice(0, splitAt),
        label: `${chunk.label} A`
      };
      const second: SceneChunk = {
        ...chunk,
        id: `${chunk.id}-split-${Date.now().toString(36)}`,
        durationSeconds: secondDuration,
        frameIds: chunk.frameIds.slice(splitAt),
        locked: false,
        label: `${chunk.label} B`
      };
      return [...chunks.slice(0, chunkIndex), first, second, ...chunks.slice(chunkIndex + 1)];
    });
    setNotice("Chunk split for provider timing.");
  }

  function handleMergeChunk(sceneId: string, chunkId: string) {
    applySceneChunks(sceneId, (chunks) => {
      const chunkIndex = chunks.findIndex((chunk) => chunk.id === chunkId);
      if (chunkIndex < 0 || chunkIndex >= chunks.length - 1) return chunks;
      const chunk = chunks[chunkIndex];
      const next = chunks[chunkIndex + 1];
      if (chunk.locked || next.locked) return chunks;
      const merged: SceneChunk = {
        ...chunk,
        durationSeconds: chunk.durationSeconds + next.durationSeconds,
        frameIds: [...chunk.frameIds, ...next.frameIds],
        enabled: chunk.enabled || next.enabled,
        label: `${chunk.label} + ${next.label}`
      };
      return [...chunks.slice(0, chunkIndex), merged, ...chunks.slice(chunkIndex + 2)];
    });
    setNotice("Adjacent chunks merged.");
  }

  function handleMoveChunk(sceneId: string, chunkId: string, direction: -1 | 1) {
    applySceneChunks(sceneId, (chunks) => {
      const chunkIndex = chunks.findIndex((chunk) => chunk.id === chunkId);
      const nextIndex = chunkIndex + direction;
      if (chunkIndex < 0 || nextIndex < 0 || nextIndex >= chunks.length) return chunks;
      const nextChunks = [...chunks];
      [nextChunks[chunkIndex], nextChunks[nextIndex]] = [nextChunks[nextIndex], nextChunks[chunkIndex]];
      return nextChunks;
    });
    setNotice("Chunk order updated.");
  }

  function updateProject(changes: Partial<FilmProject>) {
    setProject((current) => ({ ...current, ...changes }));
  }

  function repairReturnTarget(options?: { returnTo?: RepairReturnTarget }): RepairReturnTarget | undefined {
    return options?.returnTo || (showReview ? "review" : showGenerate ? "generate" : undefined);
  }

  function handleResolveMissingInput(item: MissingInput, options?: { returnTo?: RepairReturnTarget }) {
    const sourceProject = pendingProject || project;
    const returnTo = repairReturnTarget(options);
    if (isStoryboardMissingInput(item)) {
      const matchedScene = findSceneForMissingInput(sourceProject, item);
      switchToProject(sourceProject, { forceSection: "storyboards" });
      setPendingAnalysis(null);
      setPendingProject(null);
      setWorkflowView("workspace");
      setShowReview(false);
      if (matchedScene) {
        setSelectedSceneId(matchedScene.id);
        setSelectedFrameId(matchedScene.frames[0]?.id || "");
        setBoardSettings((current) => ({ ...current, scope: "current" }));
      }
      setShowGenerate(true);
      setNotice(`Resolve ${item.label}: generate, upload, or lock storyboard boards for this scene.`);
      return;
    }
    const section = sourceSectionForMissingInput(item);
    const bucket = assetBucketForMissingInput(item);
    const matchedCharacter = findCharacterForMissingInput(sourceProject, item);
    const matchedScene = findSceneForMissingInput(sourceProject, item);

    if (!section) {
      if (bucket) {
        setImportInitialBucket(bucket);
        switchToProject(sourceProject, { forceSection: "storyboards" });
        setPendingAnalysis(null);
        setPendingProject(null);
        setShowImportModal(true);
        setNotice(`Resolve ${item.label}: add ${sourceBucketLabels[bucket]} source material.`);
        return;
      }
      switchToProject(sourceProject, { forceSection: "storyboards" });
      setPendingAnalysis(null);
      setPendingProject(null);
      setShowImportModal(true);
      setNotice(`Resolve ${item.label}: import the source asset that best matches this missing input.`);
      return;
    }

    const character = matchedCharacter || (
      section === "characters"
        ? sourceProject.characters.find((item) => item.cardStatus === "missing" || item.cardSource === "missing")
        : section === "voices"
          ? sourceProject.characters.find((item) => item.voiceStatus === "missing" || item.voiceSource === "missing")
          : undefined
    );
    const scene = matchedScene || (
      section === "references"
        ? sourceProject.scenes.find((item) => item.readiness === "missing_reference")
        : undefined
    );

    switchToProject(sourceProject, { forceSection: section });
    setPendingAnalysis(null);
    setPendingProject(null);
    setWorkflowView("workspace");
    setShowGenerate(false);
    setShowReview(false);
    setSourceFocus({
      section,
      characterId: character?.id,
      characterName: character?.name || item.characterName,
      sceneId: scene?.id || item.sceneId,
      label: item.label,
      returnTo,
      returnScope: returnTo === "generate" ? boardSettings.scope : undefined,
      requestedAt: Date.now()
    });

    if (section === "characters") {
      setNotice(`Resolve ${item.label}: upload a character card or mark ${character?.name || "the character"} for EvoLink Seedance generation.`);
    } else if (section === "voices") {
      setNotice(`Resolve ${item.label}: upload actor audio or assign an EvoLink Seedance voice.`);
    } else {
      setNotice(`Resolve ${item.label}: upload a scene reference or mark the scene for EvoLink Seedance generation.`);
    }
  }

  function handleReturnFromSourceFocus(focus: SourceFocusTarget) {
    const targetSceneId = focus.sceneId && project.scenes.some((scene) => scene.id === focus.sceneId)
      ? focus.sceneId
      : selectedSceneId;
    if (targetSceneId && targetSceneId !== selectedSceneId) {
      const targetScene = project.scenes.find((scene) => scene.id === targetSceneId);
      setSelectedSceneId(targetSceneId);
      setSelectedFrameId(targetScene?.frames[0]?.id || "");
    }
    setSourceFocus(null);
    setActiveSection("storyboards");
    setWorkflowView("workspace");
    if (focus.returnTo === "generate") {
      setShowReview(false);
      setShowGenerate(true);
      setBoardSettings((current) => ({ ...current, scope: focus.returnScope || current.scope }));
      setNotice("Returned to Generate Boards. The preflight checklist will update with the latest source assignments.");
      return;
    }
    setShowGenerate(false);
    setShowReview(true);
    setNotice("Returned to Review EvoLink. Package blockers will update from the latest source assignments.");
  }

  async function handleCopyProjectLink() {
    const restoreUrl = buildProjectRestoreUrl(project.id, accessKey);
    window.history.replaceState(null, "", restoreUrl);
    try {
      await copyTextToClipboard(restoreUrl);
      setNotice("Project restore link copied. Anyone with this link can open this saved draft.");
    } catch (error) {
      setNotice("Project restore link is now in the address bar. Clipboard permission was blocked by this browser.");
    }
  }

  function buildLocalAssetRecord(bucket: AssetBucket, file: File, index: number, context: Partial<UploadedAssetRecord> = {}): UploadedAssetRecord {
    const uploadedAt = new Date().toISOString();
    return {
      assetId: `${bucket}-${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${index}`}`,
      kind: context.kind || bucket,
      label: context.label || file.name.replace(/\.[^.]+$/, ""),
      fileName: file.name,
      mimeType: file.type || "application/octet-stream",
      size: file.size,
      sourceOfTruth: context.sourceOfTruth ?? true,
      locked: context.locked ?? true,
      disabled: context.disabled ?? false,
      sourceMode: context.sourceMode || "user_upload",
      provider: context.provider || "user",
      sceneId: context.sceneId,
      characterName: context.characterName,
      uploadedAt,
      cloudStatus: "pending"
    };
  }

  function uploadedRecordFromResponse(fallback: UploadedAssetRecord, response: Awaited<ReturnType<typeof uploadProjectAsset>>): UploadedAssetRecord {
    const asset: Partial<UploadedAssetRecord> & { url?: string; type?: string } = response.asset || {};
    const localOnly = asset.localOnly ?? (response.status === "local_only" ? true : fallback.localOnly);
    return {
      ...fallback,
      ...asset,
      assetId: asset.assetId || fallback.assetId,
      projectId: asset.projectId || fallback.projectId || project.id,
      kind: asset.kind || fallback.kind,
      label: asset.label || fallback.label,
      fileName: asset.fileName || fallback.fileName,
      mimeType: asset.mimeType || asset.type || fallback.mimeType,
      size: asset.size || fallback.size,
      sourceUrl: asset.sourceUrl || asset.url || fallback.sourceUrl,
      storageKey: asset.storageKey ?? fallback.storageKey,
      sourceOfTruth: asset.sourceOfTruth ?? fallback.sourceOfTruth ?? true,
      locked: asset.locked ?? fallback.locked ?? true,
      disabled: asset.disabled ?? fallback.disabled ?? false,
      sourceMode: asset.sourceMode || fallback.sourceMode || "user_upload",
      provider: asset.provider || fallback.provider || "user",
      sceneId: asset.sceneId || fallback.sceneId,
      characterName: asset.characterName || fallback.characterName,
      uploadedAt: asset.uploadedAt || fallback.uploadedAt,
      localOnly,
      cloudStatus: asset.cloudStatus || (response.ok === false ? "local_only" : response.status || "uploaded"),
      cloudMessage: asset.cloudMessage || response.message || fallback.cloudMessage
    };
  }

  function appendUploadedAssetRecords(bucket: AssetBucket, nextRecords: UploadedAssetRecord[]) {
    setProject((current) => {
      const uploadedAssets = current.uploadedAssets || sampleProject.uploadedAssets || {
        characterCards: [],
        sceneReferences: [],
        voiceAudio: [],
        music: [],
        sfx: [],
        videoReferences: [],
        styleRefs: [],
        branding: [],
        documents: []
      };
      const nextManifest = {
        ...uploadedAssets,
        [bucket]: [...uploadedAssets[bucket], ...nextRecords]
      };
      return {
        ...current,
        uploadedAssets: nextManifest,
        // A card uploaded after analysis attaches to its character immediately.
        characters: bucket === "characterCards"
          ? reconcileCharactersWithUploads(current.characters, nextManifest)
          : current.characters
      };
    });
  }

  function replaceUploadedAssetRecord(bucket: AssetBucket, assetId: string, nextRecord: UploadedAssetRecord) {
    setProject((current) => {
      const uploadedAssets = current.uploadedAssets || emptyAssetManifest();
      const bucketRecords = uploadedAssets[bucket] || [];
      const replaced = bucketRecords.some((asset) => asset.assetId === assetId);
      return {
        ...current,
        uploadedAssets: {
          ...uploadedAssets,
          [bucket]: replaced
            ? bucketRecords.map((asset) => (asset.assetId === assetId ? nextRecord : asset))
            : [...bucketRecords, nextRecord]
        }
      };
    });
  }

  function markUploadedAssetFailed(bucket: AssetBucket, localRecord: UploadedAssetRecord, error: unknown): UploadedAssetRecord {
    const message = error instanceof Error ? error.message : String(error);
    const failedRecord = {
      ...localRecord,
      localOnly: true,
      cloudStatus: "failed",
      cloudMessage: message || "Upload failed. Retry while this browser still has the file."
    };
    replaceUploadedAssetRecord(bucket, localRecord.assetId || localRecord.label, failedRecord);
    return failedRecord;
  }

  function startAssetUpload(bucket: AssetBucket, file: File, localRecord: UploadedAssetRecord, context: Partial<UploadedAssetRecord> = {}) {
    const retryId = localRecord.assetId || localRecord.label;
    assetUploadRetryRef.current.set(retryId, { bucket, file, context });
    return uploadProjectAsset(project.id, file, localRecord)
      .then((response) => {
        if (response.ok === false) {
          throw new Error(response.message || response.status || "Asset upload failed.");
        }
        const uploadedRecord = uploadedRecordFromResponse(localRecord, response);
        replaceUploadedAssetRecord(bucket, retryId, uploadedRecord);
        assetUploadRetryRef.current.delete(retryId);
        return uploadedRecord;
      })
      .catch((error) => markUploadedAssetFailed(bucket, localRecord, error));
  }

  function handleAssetUpload(bucket: AssetBucket, files: File[], context: Partial<UploadedAssetRecord> = {}) {
    const localRecords = files.map((file, index) => buildLocalAssetRecord(bucket, file, index, context));
    appendUploadedAssetRecords(bucket, localRecords);
    setNotice(`${files.length} ${files.length === 1 ? "asset" : "assets"} added as pending source material...`);

    Promise.all(
      files.map((file, index) => startAssetUpload(bucket, file, localRecords[index], context))
    ).then((records) => {
      const cloudCount = records.filter((record) => record.cloudStatus === "uploaded").length;
      const localCount = records.filter((record) => record.cloudStatus === "local_only").length;
      const failedCount = records.filter((record) => record.cloudStatus === "failed").length;
      setNotice(
        failedCount
          ? `${failedCount} ${failedCount === 1 ? "asset" : "assets"} could not upload. Nothing was removed; retry from the source row.`
          : localCount
          ? `${records.length} ${records.length === 1 ? "asset" : "assets"} added. ${localCount} local-only until R2 is available.`
          : `${cloudCount || records.length} ${records.length === 1 ? "asset" : "assets"} uploaded and locked as source material.`
      );
    });
  }

  function handleRetryAssetUpload(bucket: AssetBucket, assetId: string) {
    const retry = assetUploadRetryRef.current.get(assetId);
    if (!retry) {
      setNotice("This browser no longer has the original file for retry. Upload the file again to replace this failed source row.");
      return;
    }
    const uploadedAssets = project.uploadedAssets || emptyAssetManifest();
    const existing = uploadedAssets[bucket].find((asset) => asset.assetId === assetId);
    if (!existing) {
      setNotice("That source row is no longer in the project.");
      return;
    }
    const pendingRecord = {
      ...existing,
      cloudStatus: "pending",
      cloudMessage: "Retrying upload..."
    };
    replaceUploadedAssetRecord(bucket, assetId, pendingRecord);
    setNotice(`Retrying ${existing.label || retry.file.name} upload. Browser autosave still has the local source row.`);
    void startAssetUpload(bucket, retry.file, pendingRecord, retry.context).then((record) => {
      if (record.cloudStatus === "failed") {
        setNotice(`${record.label || record.fileName} upload failed again: ${record.cloudMessage || "retry unavailable"}`);
      } else if (record.cloudStatus === "local_only") {
        setNotice(`${record.label || record.fileName} remains local-only until R2 is configured.`);
      } else {
        setNotice(`${record.label || record.fileName} uploaded and locked as source material.`);
      }
    });
  }

  function updateSceneReferenceAsset(assetId: string, changes: Partial<UploadedAssetRecord>) {
    setProject((current) => {
      const uploadedAssets = current.uploadedAssets || emptyAssetManifest();
      return {
        ...current,
        uploadedAssets: {
          ...uploadedAssets,
          sceneReferences: uploadedAssets.sceneReferences.map((asset) => (
            asset.assetId === assetId ? { ...asset, ...changes } : asset
          ))
        }
      };
    });
  }

  function parsedReferenceFromResponse(result: Record<string, unknown>, asset: UploadedAssetRecord): ParsedReferenceBoard {
    const source = recordValue(result.parsedReference || result);
    const rawStatus = stringValue(source.status, "parsed");
    const status = ["parsed", "failed", "not_configured", "unsupported"].includes(rawStatus)
      ? rawStatus as ParsedReferenceBoard["status"]
      : "parsed";
    const rawProvider = stringValue(source.provider, "openai");
    const provider: ParsedReferenceBoard["provider"] = rawProvider === "deterministic" ? "deterministic" : "openai";
    return {
      schema: stringValue(source.schema, "kinetix_film.reference_board_parse.v1"),
      status,
      provider,
      model: stringValue(source.model),
      parsedAt: stringValue(source.parsedAt, new Date().toISOString()),
      assetId: stringValue(source.assetId, asset.assetId || ""),
      sceneId: stringValue(source.sceneId, asset.sceneId || ""),
      title: stringValue(source.title, asset.label),
      storyWorld: stringValue(source.storyWorld),
      timePeriod: stringValue(source.timePeriod),
      location: stringValue(source.location),
      designNotes: stringArrayValue(source.designNotes),
      cameraLens: stringArrayValue(source.cameraLens),
      shotReferences: stringArrayValue(source.shotReferences),
      characterReferences: stringArrayValue(source.characterReferences),
      wardrobe: stringArrayValue(source.wardrobe),
      propsVehicles: stringArrayValue(source.propsVehicles),
      lightingAtmosphere: stringArrayValue(source.lightingAtmosphere),
      soundDesign: stringArrayValue(source.soundDesign),
      brandingWorldRules: stringArrayValue(source.brandingWorldRules),
      colorPalette: stringArrayValue(source.colorPalette),
      textTypography: stringArrayValue(source.textTypography),
      storyboardSuggestions: stringArrayValue(source.storyboardSuggestions),
      promptNotes: stringArrayValue(source.promptNotes),
      warnings: stringArrayValue(source.warnings)
    };
  }

  async function handleParseReferenceAsset(asset: UploadedAssetRecord) {
    if (!asset.assetId || parsingReferenceId === asset.assetId) return;
    const scene = project.scenes.find((item) => item.id === asset.sceneId);
    setParsingReferenceId(asset.assetId);
    updateSceneReferenceAsset(asset.assetId, { parseStatus: "parsing", parseError: "" });
    setNotice(`Analyzing ${asset.label} as a scene reference board...`);
    try {
      const result = await parseReferenceBoard({
        asset,
        scene: scene ? {
          id: scene.id,
          number: scene.number,
          slugline: scene.slugline,
          location: scene.location,
          synopsis: scene.synopsis,
          characters: scene.characters
        } : {},
        project: {
          id: project.id,
          title: project.title,
          genre: project.genre || "drama",
          goal: project.goal,
          runtimeSeconds: project.runtimeSeconds,
          aspectRatio: project.aspectRatio,
          resolution: project.resolution,
          stylePreset: project.stylePreset,
          colorGrade: project.colorGrade
        },
        scenes: project.scenes.map((item) => ({
          id: item.id,
          number: item.number,
          slugline: item.slugline,
          location: item.location,
          synopsis: item.synopsis,
          characters: item.characters
        }))
      });
      const parsedReference = parsedReferenceFromResponse(result, asset);
      const parseError = parsedReference.status === "parsed" ? "" : (parsedReference.warnings[0] || stringValue(result.message || result.error));
      updateSceneReferenceAsset(asset.assetId, {
        parseStatus: parsedReference.status,
        parsedReference,
        parsedAt: parsedReference.parsedAt,
        parseError
      });
      setNotice(parsedReference.status === "parsed"
        ? `${asset.label} parsed by OpenAI and saved to this project.`
        : `${asset.label} saved with parse fallback: ${parseError || "OpenAI was not available."}`
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      updateSceneReferenceAsset(asset.assetId, { parseStatus: "failed", parseError: message });
      setNotice(`Reference board analysis failed: ${message}`);
    } finally {
      setParsingReferenceId("");
    }
  }

  function handleSelectScene(sceneId: string) {
    const nextScene = project.scenes.find((scene) => scene.id === sceneId);
    setSelectedSceneId(sceneId);
    setSelectedFrameId(nextScene?.frames[0]?.id || "");
  }

  function handleSearchSceneSelect(sceneId: string) {
    const nextScene = project.scenes.find((scene) => scene.id === sceneId);
    if (!nextScene) {
      setNotice("Scene not found in this draft.");
      return;
    }
    handleSelectScene(sceneId);
    setActiveSection("storyboards");
    setWorkflowView("workspace");
    setShowGenerate(false);
    setShowReview(false);
    setShowPromptPreview(false);
    setShowAssetLibrary(false);
    setSourceFocus(null);
    setNotice(`Opened Scene ${String(nextScene.number).padStart(2, "0")} from project search.`);
  }

  async function runBoardGeneration(settings: BoardGenerationSettings, retryFrameIds?: string[], selectedSceneOverride?: string, extraReferenceAssets?: ReferenceAsset[], priorityReferenceAssets?: ReferenceAsset[]): Promise<BoardGenerationOutcome> {
    if (isGeneratingBoards) return { status: "skipped", generatedCount: 0, failedCount: 0, targetCount: 0, error: "A board generation job is already running." };
    const currentSceneId = selectedSceneOverride || activeScene.id;
    const scopedFrames = framesForScope(project, currentSceneId, settings.scope).filter((frame) => !frame.disabled);
    const targetFrames = eligibleFramesForGeneration(project, currentSceneId, settings, retryFrameIds);
    const targetFrameIds = targetFrames.map((frame) => frame.id);
    const skippedCount = Math.max(0, scopedFrames.length - targetFrameIds.length);

    if (settings.source === "none") {
      const timestamp = new Date().toISOString();
      const preservedCount = scopedFrames.filter((frame) => !frame.disabled && (frame.locked || frame.source === "uploaded")).length;
      const job = {
        ...createBoardGenerationJob(project, currentSceneId, settings, [], scopedFrames.length, "generated"),
        completedAt: timestamp,
        updatedAt: timestamp,
        skippedCount: scopedFrames.length,
        generatedCount: 0
      };
      setProject((current) => {
        return withGenerationJob({ ...current, stylePreset: settings.stylePreset }, job);
      });
      setShowGenerate(false);
      setNotice(`${preservedCount} uploaded or locked board${preservedCount === 1 ? "" : "s"} preserved. No generation job was sent and no open slots changed.`);
      return { status: "skipped", generatedCount: 0, failedCount: 0, targetCount: 0 };
    }

    const job = createBoardGenerationJob(project, currentSceneId, settings, targetFrameIds, skippedCount);
    if (!targetFrameIds.length) {
      const timestamp = new Date().toISOString();
      const completedJob = {
        ...job,
        status: "generated" as const,
        updatedAt: timestamp,
        completedAt: timestamp,
        skippedCount: scopedFrames.length
      };
      setProject((current) => withGenerationJob({ ...current, stylePreset: settings.stylePreset }, completedJob));
      setShowGenerate(false);
      setNotice("No open board slots to generate. Uploaded, locked, and disabled boards were preserved.");
      return { status: "skipped", generatedCount: 0, failedCount: 0, targetCount: 0 };
    }

    setProject((current) => withFrameGenerationStatus(withGenerationJob(current, job), targetFrameIds, "queued", job.id));
    setIsGeneratingBoards(true);
    setNotice(`${boardSourceLabel(settings.source)} job queued for ${targetFrameIds.length} board${targetFrameIds.length === 1 ? "" : "s"}...`);

    try {
      setProject((current) => withFrameGenerationStatus(withGenerationJobPatch(current, job.id, { status: "generating" }), targetFrameIds, "generating", job.id));
      setNotice(settings.source === "chatgpt_image" ? "Generating storyboard images with ChatGPT Image..." : "Building internal storyboard sketches...");
      // Priority refs (e.g. a look chosen for one scene) go first — dedupe keeps the first per
      // character/scene, so a chosen look beats the manifest's default card.
      const referenceAssets = settings.source === "chatgpt_image"
        ? dedupeReferenceAssets([...(priorityReferenceAssets || []), ...referenceAssetsFromManifest(project.uploadedAssets), ...(extraReferenceAssets || [])])
        : [];
      const storyboard = await buildStoryboard({
        plan: scopedApiPlan(project, currentSceneId, settings.scope),
        settings: {
          coverage: "full",
          sceneSelection: scopeToSceneSelection(project, currentSceneId, settings.scope),
          style: settings.stylePreset,
          storyboardProvider: settings.source,
          panelsPerScene: settings.panelsPerScene,
          projectId: project.id,
          frameTargets: frameTargetsForGeneration(project, targetFrames),
          referenceAssets,
          sourceMode: "generate",
          respectLocks: true,
          imageQuality: settings.imageQuality || defaultBoardSettings.imageQuality,
          imageSize: resolveImageSize(settings.imageSize, project.aspectRatio)
        }
      });
      const imageGeneration = storyboard.imageGeneration as { generatedCount?: number; skippedCount?: number; status?: string; warnings?: string[] } | null | undefined;
      const storyboardFrames = Array.isArray(storyboard.frames) ? storyboard.frames : [];
      const generatedCount = settings.source === "chatgpt_image"
        ? Number(imageGeneration?.generatedCount || 0)
        : storyboardFrames.length || targetFrameIds.length;
      const apiSkippedCount = Number(imageGeneration?.skippedCount ?? skippedCount);
      const completedSkippedCount = Math.max(skippedCount, Number.isFinite(apiSkippedCount) ? apiSkippedCount : 0);
      const imageStatus = String(imageGeneration?.status || "");
      const warnings = Array.isArray(imageGeneration?.warnings) ? imageGeneration.warnings.filter(Boolean) : [];
      const framesByTargetId = new Map(
        storyboardFrames.map((frame) => {
          const record = recordValue(frame);
          return [stringValue(record.frameId || record.id), record];
        })
      );
      const failedTargetFrameIds = settings.source === "chatgpt_image"
        ? targetFrameIds.filter((frameId, index) => {
            const apiFrame = framesByTargetId.get(frameId) || recordValue(storyboardFrames[index]);
            const image = isRecord(apiFrame.image) ? apiFrame.image : {};
            return String(image.status || "") !== "generated";
          })
        : [];
      const failedTargetFrameSet = new Set(failedTargetFrameIds);
      const successfulTargetFrameIds = targetFrameIds.filter((frameId) => !failedTargetFrameSet.has(frameId));
      const hasChatGptFailures = settings.source === "chatgpt_image" && failedTargetFrameIds.length > 0;
      const chatGptPromptFallback = settings.source === "chatgpt_image" && generatedCount === 0 && imageStatus !== "generated";
      const preservedNotice = completedSkippedCount
        ? ` ${completedSkippedCount} protected or disabled board${completedSkippedCount === 1 ? "" : "s"} preserved.`
        : "";
      const completedAt = new Date().toISOString();
      const errorMessage = hasChatGptFailures
        ? warnings[0] || `${generatedCount} of ${targetFrameIds.length} ChatGPT storyboard images generated. Retry or upload the remaining board${failedTargetFrameIds.length === 1 ? "" : "s"}.`
        : undefined;

      setProject((current) => {
        const withStoryboard = storyboardToFilmProject(storyboard, { ...current, stylePreset: settings.stylePreset }, { targetFrameIds });
        const withSuccessfulFrames = successfulTargetFrameIds.length
          ? withFrameGenerationStatus(withStoryboard, successfulTargetFrameIds, boardGenerationSuccessStatus(settings.source), job.id, { timestamp: completedAt })
          : withStoryboard;
        const withFrames = failedTargetFrameIds.length
          ? withFrameGenerationStatus(withSuccessfulFrames, failedTargetFrameIds, "failed", job.id, { error: errorMessage, timestamp: completedAt })
          : withSuccessfulFrames;
        return withGenerationJobPatch(withFrames, job.id, {
          status: hasChatGptFailures ? "failed" : "generated",
          generatedCount,
          skippedCount: completedSkippedCount,
          completedAt,
          error: errorMessage
        });
      });

      if (hasChatGptFailures) {
        setShowGenerate(true);
        setNotice(chatGptPromptFallback
          ? `${errorMessage}${preservedNotice} Prompts were saved on the failed board slots; retry or upload boards instead.`
          : `${generatedCount} ChatGPT storyboard image${generatedCount === 1 ? "" : "s"} generated. ${failedTargetFrameIds.length} board${failedTargetFrameIds.length === 1 ? "" : "s"} need retry or upload.${preservedNotice}`
        );
      } else {
        setShowGenerate(false);
        if (settings.source === "chatgpt_image") {
          setNotice(`${generatedCount} ChatGPT storyboard image${generatedCount === 1 ? "" : "s"} generated and saved.${preservedNotice}`);
        } else {
          setNotice(`${generatedCount} internal storyboard sketch${generatedCount === 1 ? "" : "es"} generated and saved.${preservedNotice}`);
        }
      }
      return {
        status: hasChatGptFailures ? "failed" : "generated",
        generatedCount,
        failedCount: failedTargetFrameIds.length,
        targetCount: targetFrameIds.length,
        error: errorMessage
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      const completedAt = new Date().toISOString();
      setProject((current) => {
        const withFrames = withFrameGenerationStatus(current, targetFrameIds, "failed", job.id, { error: message, timestamp: completedAt });
        return withGenerationJobPatch(withFrames, job.id, {
          status: "failed",
          error: message,
          completedAt,
          skippedCount
        });
      });
      setShowGenerate(true);
      setNotice(`Storyboard generation failed: ${message}. Retry or upload boards instead.`);
      return { status: "failed", generatedCount: 0, failedCount: targetFrameIds.length, targetCount: targetFrameIds.length, error: message };
    } finally {
      setIsGeneratingBoards(false);
    }
  }

  function handleGenerateBoards() {
    void runBoardGeneration(boardSettings);
  }

  /** Snapshot a scene's current boards as a take (newest first, capped) before they're overwritten. */
  function snapshotSceneTake(sceneId: string) {
    setProject((current) => {
      const scene = current.scenes.find((item) => item.id === sceneId);
      if (!scene) return current;
      const frames = scene.frames
        .filter((frame) => !frame.disabled && (frame.imageUrl || frame.sourceUrl || frame.storageKey))
        .map((frame) => ({
          id: frame.id,
          label: frame.label,
          shotType: frame.shotType,
          durationSeconds: frame.durationSeconds,
          imageUrl: frame.imageUrl,
          sourceUrl: frame.sourceUrl,
          storageKey: frame.storageKey,
          prompt: frame.prompt
        }));
      if (!frames.length) return current;
      const existing = current.sceneTakes?.[sceneId] || [];
      const take = {
        id: `take_${Date.now().toString(36)}`,
        sceneId,
        createdAt: new Date().toISOString(),
        label: `Take ${existing.length + 1}`,
        frames
      };
      return {
        ...current,
        sceneTakes: { ...(current.sceneTakes || {}), [sceneId]: [take, ...existing].slice(0, 4) }
      };
    });
  }

  /** Restore a previous take's boards onto the scene (locked/uploaded frames are never touched). */
  function handleRestoreTake(sceneId: string, takeId: string) {
    snapshotSceneTake(sceneId); // the current state becomes a take too, so A/B is reversible
    setProject((current) => {
      const take = (current.sceneTakes?.[sceneId] || []).find((item) => item.id === takeId);
      if (!take) return current;
      return {
        ...current,
        scenes: current.scenes.map((scene) => {
          if (scene.id !== sceneId) return scene;
          return {
            ...scene,
            frames: scene.frames.map((frame, index) => {
              if (frame.locked || frame.source === "uploaded") return frame;
              const source = take.frames.find((item) => item.id === frame.id) || take.frames[index];
              if (!source) return frame;
              return {
                ...frame,
                label: source.label || frame.label,
                shotType: source.shotType || frame.shotType,
                durationSeconds: source.durationSeconds || frame.durationSeconds,
                imageUrl: source.imageUrl,
                sourceUrl: source.sourceUrl,
                storageKey: source.storageKey,
                prompt: source.prompt ?? frame.prompt,
                source: "generated",
                generationStatus: "generated"
              };
            })
          };
        })
      };
    });
    setNotice("Previous take restored for this scene. The newer boards are saved as a take too.");
  }

  /** One-tap film poster: hero card + a world plate as conditioning references. */

  /** Publish a read-only snapshot and put the link on the clipboard. */
  const [isSharingLink, setIsSharingLink] = useState(false);
  async function handleShareLink() {
    if (isSharingLink) return;
    setIsSharingLink(true);
    try {
      const response = await createShareSnapshot(project as unknown as Record<string, unknown>);
      if (!response.ok || !response.token) {
        setNotice(response.message || "Couldn't create a share link — is the engine running?");
        return;
      }
      const url = `${window.location.origin}${window.location.pathname}?share=${response.token}`;
      try {
        await navigator.clipboard.writeText(url);
        setNotice("Share link copied — anyone with it can watch the cut and see the pitch.");
      } catch {
        setNotice(`Share link ready: ${url}`);
      }
    } catch {
      setNotice("Couldn't create a share link — is the engine running?");
    } finally {
      setIsSharingLink(false);
    }
  }

  async function handleMakePoster() {
    if (isMakingPoster) return;
    setIsMakingPoster(true);
    setNotice("Making your film poster (~30s)...");
    try {
      const people = matchPeople(project.characters, project.uploadedAssets);
      const places = matchPlaces(project.scenes, project.uploadedAssets);
      const hero = people.filter((person) => person.inScript && person.defaultLook).sort((a, b) => b.looks.length - a.looks.length)[0];
      const world = places.find((place) => place.defaultLook);
      const references = [hero?.defaultLook, world?.defaultLook]
        .filter((look): look is NonNullable<typeof look> => Boolean(look))
        .map((look) => ({ storageKey: look.asset.storageKey, sourceUrl: look.asset.sourceUrl }));
      const pitchKit = (project.pitchKit || {}) as Record<string, unknown>;
      const posterCopy = (pitchKit.posterCopy && typeof pitchKit.posterCopy === "object" ? pitchKit.posterCopy : {}) as Record<string, unknown>;
      const pitchLogline = Array.isArray(pitchKit.loglines) && typeof pitchKit.loglines[0] === "string" ? pitchKit.loglines[0] : "";
      const posterTag = typeof posterCopy.tag === "string" ? posterCopy.tag : "";
      const result = await generatePoster({
        projectId: project.id,
        title: (typeof posterCopy.title === "string" && posterCopy.title) || project.title,
        genre: project.genre || "",
        tone: [projectTone(project), posterTag].filter(Boolean).join(" — "),
        logline: pitchLogline || projectLogline(project),
        style: project.stylePreset,
        references,
        settings: { imageQuality: easyModeOptions.imageQuality }
      });
      const poster = isRecord(result.poster) ? (result.poster as unknown as UploadedAssetRecord) : null;
      if (String(result.status) === "generated" && poster) {
        setProject((current) => ({ ...current, posterAsset: poster }));
        setNotice(`Poster ready${result.conditioned ? " — starring your cast and world" : ""}.`);
      } else {
        const warning = Array.isArray(result.warnings) ? String(result.warnings[0] || "") : "";
        setNotice(`Poster could not be generated${warning ? `: ${warning}` : "."}`);
      }
    } catch (error) {
      setNotice(`Poster failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsMakingPoster(false);
    }
  }

  /** Simple mode "Send to render": package the whole film as a dry run after in-sheet rights confirmation. */
  async function handleSimplePackage(): Promise<boolean> {
    const fullGates: ReviewGateState = {
      scriptRights: true,
      likenessRights: true,
      voiceRights: true,
      brandIpRights: true,
      humanReview: true,
      finalConfirm: true
    };
    setReviewGateState(fullGates);
    setReviewScope("all");
    return handleSaveDryRunPackage("save", { scope: "all", categories: defaultCategories, gateState: fullGates });
  }

  /** Director's notes: free text per scene, autosaved with the project and printed with the pitch. */
  function handleSaveSceneNote(sceneId: string, text: string) {
    setProject((current) => ({
      ...current,
      sceneNotes: { ...(current.sceneNotes || {}), [sceneId]: text }
    }));
  }

  /** Pro Glass: assign a Seedance preset — uploads still outrank it at package time. */
  function handleSetVoicePreset(characterId: string, preset: SeedanceVoicePreset) {
    setProject((current) => ({
      ...current,
      characters: current.characters.map((character) =>
        character.id === characterId
          ? { ...character, voicePreset: preset, voiceStatus: "assigned" as const, voiceSource: "seed_generated" as const }
          : character
      )
    }));
    setNotice(`Voice preset saved — performed by Seedance at render time.`);
  }

  /** Pro Glass: fill open/failed slots in one scene without snapshotting a take.
      Slots are expanded to the requested count first; generation fires after commit. */
  const [pendingSceneGen, setPendingSceneGen] = useState<{
    sceneId: string;
    frameIds: string[];
    settings: BoardGenerationSettings;
    priorityRefs?: ReferenceAsset[];
  } | null>(null);
  useEffect(() => {
    if (!pendingSceneGen || isGeneratingBoards) return;
    const { sceneId, frameIds, settings, priorityRefs } = pendingSceneGen;
    setPendingSceneGen(null);
    void runBoardGeneration(settings, frameIds.length ? frameIds : undefined, sceneId, undefined, priorityRefs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSceneGen, isGeneratingBoards]);

  function handleGenerateScene(sceneId: string, panelsPerScene: number) {
    const settings: BoardGenerationSettings = {
      ...boardSettings,
      scope: "current",
      source: "chatgpt_image",
      panelsPerScene
    };
    setBoardSettings(settings);
    const expansion = expandScenePanels(project, sceneId, panelsPerScene);
    if (expansion.project !== project) setProject(expansion.project);
    setPendingSceneGen({ sceneId, frameIds: expansion.frameIds, settings });
  }

  /** Simple mode "Re-make this scene": chosen looks become priority conditioning refs for that scene only.
      When request.frameIds is set, only those boards regenerate (re-make just this shot). */
  function handleSimpleSceneRemake(request: SimpleSceneRemakeRequest) {
    snapshotSceneTake(request.sceneId);
    const priorityRefs: ReferenceAsset[] = Object.entries(request.lookOverrides).map(([characterName, look]) => ({
      kind: "characterCard",
      characterName,
      storageKey: look.asset.storageKey,
      sourceUrl: look.asset.sourceUrl
    }));
    if (request.placeLook) {
      priorityRefs.push({
        kind: "sceneReference",
        sceneId: request.sceneId,
        storageKey: request.placeLook.asset.storageKey,
        sourceUrl: request.placeLook.asset.sourceUrl
      });
    }
    const remakeSettings: BoardGenerationSettings = {
      ...boardSettings,
      scope: "current",
      source: "chatgpt_image",
      panelsPerScene: request.panelsPerScene ?? boardSettings.panelsPerScene
    };
    setBoardSettings(remakeSettings);
    if (request.frameIds?.length) {
      void runBoardGeneration(remakeSettings, request.frameIds, request.sceneId, undefined, dedupeReferenceAssets(priorityRefs));
      return;
    }
    const expansion = expandScenePanels(project, request.sceneId, remakeSettings.panelsPerScene, true);
    if (expansion.project !== project) setProject(expansion.project);
    setPendingSceneGen({
      sceneId: request.sceneId,
      frameIds: expansion.frameIds,
      settings: remakeSettings,
      priorityRefs: dedupeReferenceAssets(priorityRefs)
    });
  }

  async function handleEasyMode(optionsOverride?: EasyModeOptions) {
    if (isEasyModeRunning || isGeneratingBoards || isAnalyzing) return;
    setIsEasyModeRunning(true);
    const runOptions = optionsOverride ?? easyModeOptions;

    // Resolve the run scope: the whole script or a single scene picked in the Easy Mode popover.
    const scopedScenes = scenesForEasyModeScope(project.scenes, runOptions.sceneScope);
    const scopedScene = runOptions.sceneScope !== "all" ? scopedScenes[0] : undefined;
    const scopeLabel = scopedScene ? `Scene ${String(scopedScene.number).padStart(2, "0")}` : "All scenes";
    const scopedCharacters = scopedScene ? charactersInScenes(project.characters, scopedScenes) : project.characters;
    const imageQuality = runOptions.imageQuality;
    // Plates and storyboard frames follow the project frame; character cards stay square identity sheets.
    const frameImageSize = resolveImageSize(runOptions.imageSize, project.aspectRatio);

    const voicesNeeded = project.characters.filter((character) => !character.voiceLocked && !character.voiceDisabled && character.voiceStatus !== "assigned").length;
    const cardsEligible = scopedCharacters.filter((character) => !character.cardLocked && character.cardStatus !== "uploaded").length;
    const claimedSceneRefsUpfront = new Set(
      (project.uploadedAssets?.sceneReferences || [])
        .map((record) => (record.sceneId || "").trim().toLowerCase())
        .filter((value) => value.length > 0)
    );
    const platesEligible = scopedScenes.filter((scene) => !claimedSceneRefsUpfront.has((scene.id || "").trim().toLowerCase())).length;
    const boardsInScope = scopedScenes.reduce((total, scene) => total + scene.frames.filter((frame) => !frame.disabled).length, 0);

    let progress = createEasyModeProgress(scopeLabel, {
      voices: `${project.characters.length} character${project.characters.length === 1 ? "" : "s"}`,
      cards: `${cardsEligible} to generate`,
      plates: `${platesEligible} of ${scopedScenes.length} scene${scopedScenes.length === 1 ? "" : "s"}`,
      storyboard: `${boardsInScope} board${boardsInScope === 1 ? "" : "s"}`
    });
    const pushProgress = (next: EasyModeProgress) => {
      progress = next;
      setEasyModeProgress(next);
    };
    pushProgress(patchEasyModeStep(progress, "voices", { status: "running" }));
    setNotice(`Easy Mode (${scopeLabel}): auto-assigning Seedance voices...`);
    try {
      // 1) Auto-assign a Seedance voice preset to every character that still needs one.
      //    Uploaded, locked, and disabled voices are always preserved.
      setProject((current) => ({ ...current, characters: planEasyModeVoices(current.characters) }));
      pushProgress(patchEasyModeStep(progress, "voices", {
        status: "done",
        detail: voicesNeeded ? `${voicesNeeded} assigned` : "all set"
      }));

      // 2) Generate a ChatGPT Image character card for every eligible character in scope.
      //    Uploads and locks are the source of truth and are never overwritten.
      pushProgress(patchEasyModeStep(progress, "cards", { status: "running" }));
      setNotice("Easy Mode: generating character cards with ChatGPT Image...");
      const cardPayloadCharacters = scopedCharacters.map((character) => ({
        id: character.id,
        name: character.name,
        role: character.role,
        description: character.seedCharacterPrompt || "",
        cardStatus: character.cardStatus,
        locked: Boolean(character.cardLocked),
        sourceOfTruth: character.cardStatus === "uploaded"
      }));
      const easyWarnings: string[] = [];
      // Accumulate freshly generated references locally — setProject is async, so the project closure
      // below stays stale; we hand these directly to the conditioner instead of reading them back.
      const freshReferences: ReferenceAsset[] = [];
      const runStyle = runOptions.stylePreset || project.stylePreset;
      try {
        const cardResult = await generateCharacterCards({
          projectId: project.id,
          settings: { projectId: project.id, style: runStyle, imageQuality, imageSize: "1024x1024" },
          characters: cardPayloadCharacters
        });
        const cards = Array.isArray(cardResult.cards) ? (cardResult.cards as GeneratedCardResult[]) : [];
        if (cards.length) {
          setProject((current) => {
            const manifest = current.uploadedAssets || emptyAssetManifest();
            return {
              ...current,
              characters: applyGeneratedCardsToCharacters(current.characters, cards),
              uploadedAssets: {
                ...manifest,
                characterCards: applyGeneratedCardsToManifest(manifest.characterCards || [], cards)
              }
            };
          });
          for (const card of cards) {
            if (String(card.cardStatus || "") !== "generated") continue;
            const src = card.sourceUrl || card.imageUrl || "";
            if (!card.storageKey && !src) continue;
            freshReferences.push({ kind: "characterCard", characterName: card.name, storageKey: card.storageKey, sourceUrl: src });
          }
        }
        easyWarnings.push(...(Array.isArray(cardResult.warnings) ? (cardResult.warnings as string[]).filter(Boolean) : []));
        const generatedCount = Number(cardResult.generatedCount || 0);
        const cardWarningCount = Array.isArray(cardResult.warnings) ? (cardResult.warnings as string[]).filter(Boolean).length : 0;
        pushProgress(patchEasyModeStep(progress, "cards", {
          status: cardWarningCount ? "warning" : "done",
          detail: cardsEligible ? `${generatedCount} of ${cardsEligible} generated` : "all covered"
        }));
        setNotice(`Easy Mode: ${generatedCount} character card${generatedCount === 1 ? "" : "s"} generated.`);
      } catch (cardError) {
        const message = cardError instanceof Error ? cardError.message : String(cardError);
        easyWarnings.push(message);
        pushProgress(patchEasyModeStep(progress, "cards", { status: "failed", detail: "failed — continuing" }));
        setNotice(`Easy Mode: character cards could not be generated (${message}). Continuing...`);
      }

      // 3) Generate an environment/setting plate per scoped scene — the backdrop the characters live in.
      //    Scenes that already have an uploaded reference keep it (uploads win); only empty scenes are filled.
      pushProgress(patchEasyModeStep(progress, "plates", { status: "running" }));
      setNotice("Easy Mode: generating scene environment plates with ChatGPT Image...");
      const claimedSceneRefs = new Set(
        (project.uploadedAssets?.sceneReferences || [])
          .map((record) => (record.sceneId || "").trim().toLowerCase())
          .filter((value) => value.length > 0)
      );
      const scenePayload = scopedScenes.map((scene) => ({
        id: scene.id,
        heading: scene.slugline,
        slugline: scene.slugline,
        location: scene.location,
        summary: scene.synopsis,
        referenceStatus: claimedSceneRefs.has((scene.id || "").trim().toLowerCase()) ? "uploaded" : "missing"
      }));
      try {
        const sceneResult = await generateSceneReferences({
          projectId: project.id,
          settings: { projectId: project.id, style: runStyle, imageQuality, imageSize: frameImageSize },
          scenes: scenePayload
        });
        const plates = Array.isArray(sceneResult.plates) ? (sceneResult.plates as GeneratedScenePlate[]) : [];
        if (plates.length) {
          setProject((current) => {
            const manifest = current.uploadedAssets || emptyAssetManifest();
            return {
              ...current,
              uploadedAssets: {
                ...manifest,
                sceneReferences: applyGeneratedScenePlates(manifest.sceneReferences || [], plates)
              }
            };
          });
          for (const plate of plates) {
            if (String(plate.referenceStatus || "") !== "generated") continue;
            const src = plate.sourceUrl || plate.imageUrl || "";
            if (!plate.storageKey && !src) continue;
            freshReferences.push({ kind: "sceneReference", sceneId: plate.sceneId, storageKey: plate.storageKey, sourceUrl: src });
          }
        }
        easyWarnings.push(...(Array.isArray(sceneResult.warnings) ? (sceneResult.warnings as string[]).filter(Boolean) : []));
        const generatedScenes = Number(sceneResult.generatedCount || 0);
        const plateWarningCount = Array.isArray(sceneResult.warnings) ? (sceneResult.warnings as string[]).filter(Boolean).length : 0;
        pushProgress(patchEasyModeStep(progress, "plates", {
          status: plateWarningCount ? "warning" : "done",
          detail: platesEligible ? `${generatedScenes} of ${platesEligible} generated` : "all covered"
        }));
        setNotice(`Easy Mode: ${generatedScenes} scene plate${generatedScenes === 1 ? "" : "s"} generated.`);
      } catch (sceneError) {
        const message = sceneError instanceof Error ? sceneError.message : String(sceneError);
        easyWarnings.push(message);
        pushProgress(patchEasyModeStep(progress, "plates", { status: "failed", detail: "failed — continuing" }));
        setNotice(`Easy Mode: scene plates could not be generated (${message}). Continuing...`);
      }

      // 4) Assemble + generate the scoped storyboard with ChatGPT Image.
      pushProgress(patchEasyModeStep(progress, "storyboard", { status: "running" }));
      setNotice("Easy Mode: assembling storyboard scenes with ChatGPT Image...");
      const easySettings: BoardGenerationSettings = {
        ...boardSettings,
        scope: scopedScene ? "current" : "all",
        source: "chatgpt_image",
        stylePreset: runStyle,
        imageQuality,
        imageSize: runOptions.imageSize
      };
      setBoardSettings(easySettings);
      const boardOutcome = await runBoardGeneration(
        easySettings,
        undefined,
        scopedScene?.id,
        dedupeReferenceAssets(freshReferences)
      );
      if (boardOutcome.error) easyWarnings.push(boardOutcome.error);
      pushProgress(patchEasyModeStep(progress, "storyboard", {
        status: boardOutcome.status === "generated" ? "done" : boardOutcome.status === "skipped" ? "done" : boardOutcome.failedCount && boardOutcome.generatedCount ? "warning" : "failed",
        detail: boardOutcome.status === "skipped"
          ? "no open slots"
          : `${boardOutcome.generatedCount} of ${boardOutcome.targetCount} generated${boardOutcome.failedCount ? ` · ${boardOutcome.failedCount} need retry` : ""}`
      }));

      // 5) Land the user in the storyboard review workspace to adjust grading/format before render.
      setActiveSection("storyboards");
      const warningSuffix = easyWarnings.length
        ? ` ${easyWarnings.length} warning${easyWarnings.length === 1 ? "" : "s"} — review before render.`
        : "";
      pushProgress(finishEasyModeProgress(progress, easyWarnings));
      setNotice(`Easy Mode complete (${scopeLabel}): voices assigned, character cards and scene plates generated, and storyboard built. Adjust color grading or format before render.${warningSuffix}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushProgress(finishEasyModeProgress(
        patchEasyModeStep(progress, progress.steps.find((step) => step.status === "running")?.id || "storyboard", { status: "failed", detail: "stopped" }),
        [message]
      ));
      setNotice(`Easy Mode stopped: ${message}. Your uploads and locked items were preserved.`);
    } finally {
      setIsEasyModeRunning(false);
    }
  }

  function handleRetryGenerationJob(jobId: string) {
    const job = project.generationJobs?.find((item) => item.id === jobId);
    if (!job) {
      setNotice("Generation job not found in this draft.");
      return;
    }
    const retrySettings: BoardGenerationSettings = {
      scope: job.scope,
      source: job.source,
      stylePreset: job.stylePreset,
      panelsPerScene: job.panelsPerScene
    };
    setBoardSettings(retrySettings);
    setShowGenerate(true);
    void runBoardGeneration(retrySettings, job.frameIds, job.sceneIds[0] || activeScene.id);
  }

  function handleUploadInsteadForGenerationJob(jobId: string) {
    const job = project.generationJobs?.find((item) => item.id === jobId);
    if (!job) {
      setNotice("Generation job not found in this draft.");
      return;
    }
    const targetIds = new Set(job.frameIds);
    const jobFrames = project.scenes.flatMap((scene) => scene.frames.map((frame) => ({ sceneId: scene.id, frame })));
    const preferredFrame = jobFrames.find(({ frame }) => targetIds.has(frame.id) && frame.generationStatus === "failed")
      || jobFrames.find(({ frame }) => targetIds.has(frame.id));
    if (!preferredFrame) {
      setNotice("No failed board slot was found for that job.");
      return;
    }
    setSelectedSceneId(preferredFrame.sceneId);
    setSelectedFrameId(preferredFrame.frame.id);
    setActiveSection("storyboards");
    setWorkflowView("workspace");
    setShowGenerate(false);
    setShowReview(false);
    setNotice("Selected the failed board. Use Upload instead to replace it without retrying generation.");
  }

  async function handleScriptUpload(file: File) {
    const upload = await readScriptUpload(file);
    setProject((current) => ({
      ...current,
      scriptUpload: upload,
      scriptExcerpt: upload.text || current.scriptExcerpt
    }));
    setNotice(`${file.name} loaded. Analyze when ready.`);
  }

  async function handleAnalyzeScript(options: { autoApprove?: boolean } = {}): Promise<boolean> {
    if (isAnalyzing) return false;
    setIsAnalyzing(true);
    setNotice("Analyzing script with the Kinetix Production Agent...");
    const scriptUpload = project.scriptUpload;
    try {
      const analysis = await analyzeFilmProject({
        title: project.title,
        genre: project.genre || "drama",
        projectGoal: project.goal,
        runtime: secondsToRuntime(project.runtimeSeconds),
        scriptFileName: scriptUpload?.fileName || "",
        scriptText: scriptUpload?.text || project.scriptExcerpt,
        scriptFileDataBase64: scriptUpload?.dataBase64 || "",
        useOpenAI: true,
        uploadedAssets: project.uploadedAssets || sampleProject.uploadedAssets
      });
      const parsedProject = analysisToFilmProject(analysis, project);
      // Attach the user's uploads to the freshly parsed cast — a character with a matching
      // uploaded card is "uploaded", not "missing", so nothing the user gave us shows as unresolved.
      const nextProject = {
        ...parsedProject,
        characters: reconcileCharactersWithUploads(parsedProject.characters, parsedProject.uploadedAssets)
      };
      if (options.autoApprove) {
        // Simple mode: the "Here's your film" screen IS the review — commit the parse directly.
        switchToProject(nextProject, { forceSection: "storyboards" });
        setPendingAnalysis(null);
        setPendingProject(null);
        setShowGenerate(false);
        setShowReview(false);
        setNotice(`${nextProject.scenes.length} scene${nextProject.scenes.length === 1 ? "" : "s"} parsed and committed.`);
        return true;
      }
      setPendingAnalysis(analysis);
      setPendingProject(nextProject);
      setWorkflowView("parseReview");
      setShowGenerate(false);
      setShowReview(false);
      const warnings = Array.isArray(analysis.warnings) ? analysis.warnings : [];
      const notices = Array.isArray(analysis.notices) ? analysis.notices : [];
      setNotice(
        warnings[0]
          ? String(warnings[0])
          : notices[0]
            ? `${String(notices[0])} Review the parse before committing it.`
            : `Analysis ready for review: ${nextProject.scenes.length} scene${nextProject.scenes.length === 1 ? "" : "s"} parsed.`
      );
      return true;
    } catch (error) {
      setNotice(`Analysis API unavailable: ${error instanceof Error ? error.message : String(error)}. Browser autosave still has your project.`);
      return false;
    } finally {
      setIsAnalyzing(false);
    }
  }

  // "Just make it": the whole pipeline from wherever the project is — analyze (auto-approved) when
  // needed, then Easy Mode with smart defaults. The pending flag lets the chain pick up the freshly
  // committed project state on the next render instead of a stale closure.
  const [justMakePending, setJustMakePending] = useState(false);

  function handleJustMakeIt() {
    if (isEasyModeRunning || isAnalyzing || isGeneratingBoards) return;
    const parsed = project.characters.length > 0;
    if (parsed) {
      void handleEasyMode({ ...easyModeOptions, sceneScope: "all" });
      return;
    }
    setJustMakePending(true);
    void handleAnalyzeScript({ autoApprove: true }).then((ok) => {
      if (!ok) setJustMakePending(false);
    });
  }

  useEffect(() => {
    if (!justMakePending || isAnalyzing || isEasyModeRunning) return;
    if (project.characters.length === 0) return;
    setJustMakePending(false);
    void handleEasyMode({ ...easyModeOptions, sceneScope: "all" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justMakePending, isAnalyzing, isEasyModeRunning, project.characters.length]);

  // "Try a sample script": load the bundled screenplay, then run the one-tap chain on it.
  const [samplePending, setSamplePending] = useState(false);

  function handleTrySample() {
    if (isAnalyzing || isEasyModeRunning) return;
    const file = new File([SAMPLE_SCRIPT_TEXT], SAMPLE_SCRIPT_FILE_NAME, { type: "text/plain" });
    setSamplePending(true);
    handleScriptUpload(file).catch((error) => {
      setSamplePending(false);
      setNotice(`Sample script failed to load: ${error instanceof Error ? error.message : String(error)}`);
    });
  }

  useEffect(() => {
    if (!samplePending) return;
    if (project.scriptUpload?.fileName !== SAMPLE_SCRIPT_FILE_NAME) return;
    setSamplePending(false);
    handleJustMakeIt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [samplePending, project.scriptUpload?.fileName]);

  function handleApproveParse() {
    if (!pendingAnalysis || !pendingProject) return;
    setWorkflowView("aiPlan");
    setNotice("Parse approved. Review the Production Agent plan before generating boards.");
  }

  function handleApprovePlan() {
    if (!pendingProject) return;
    switchToProject(pendingProject, { forceSection: "storyboards" });
    setPendingAnalysis(null);
    setPendingProject(null);
    setShowGenerate(true);
    setShowReview(false);
    setNotice("Production plan approved. Choose storyboard source, scope, and generation settings.");
  }

  async function handleSaveDryRunPackage(
    intent: PackageIntent = "save",
    options?: { scope?: ExportScope; categories?: EvoLinkCategory[]; gateState?: ReviewGateState }
  ): Promise<boolean> {
    if (isSavingPackage) return false;
    // Simple mode packages the whole film in one tap; Pro passes nothing and uses the review panel state.
    const pkgScope = options?.scope ?? reviewScope;
    const pkgCategories = options?.categories ?? reviewCategories;
    const pkgGateState = options?.gateState ?? reviewGateState;
    setIsSavingPackage(true);
    setNotice(intent === "send_test"
      ? "Preparing 480p dry-run test package. No provider credits are spent."
      : "Building EvoLink dry-run package. No provider credits are spent."
    );
    const scopedPlan = scopedApiPlan(project, activeScene.id, pkgScope);
    const scopeScenes = scenesForScope(project, activeScene.id, pkgScope);
    const scopeSceneIds = scopeScenes.map((scene) => scene.id);
    const sceneSelection = scopeToSceneSelection(project, activeScene.id, pkgScope);
    const packageCategories = categoriesToPackageSettings(pkgCategories);
    const uiMissingInputs = reviewMissingInputsForScenes(project, scopeScenes, { includeBoards: pkgCategories.includes("boards") });
    const packageMissingRequired = uiMissingInputs.map(missingInputToPackageRequired);
    const packageCreatedAt = new Date().toISOString();
    const sourceMap = buildPackageSourceMapSnapshot(project, activeScene.id, pkgScope, pkgCategories, packageCreatedAt);
    const packageAgentSummary = agentPlanSummary(project.apiPlan || {}, project);
    const packageAgentStrategy = {
      goal: packageAgentSummary.goal,
      summary: packageAgentSummary.strategy,
      confidence: packageAgentSummary.confidence / 100,
      confidenceLabel: `${packageAgentSummary.confidence}%`,
      objective: packageAgentSummary.objective,
      runtimeSeconds: packageAgentSummary.runtimeSeconds,
      chunkSeconds: packageAgentSummary.chunkSeconds,
      chunkCount: packageAgentSummary.chunkCount,
      beatCount: packageAgentSummary.beats.length,
      recommendedSceneIds: [...new Set(project.agentBeats.map((beat) => beat.sceneId).filter(Boolean))]
    };
    try {
      const result = await buildProductionPackage({
        plan: scopedPlan,
        settings: {
          projectId: project.id,
          title: project.title,
          genre: project.genre || "drama",
          projectGoal: project.goal,
          quality: project.resolution,
          aspectRatio: project.aspectRatio,
          scopeType: pkgScope,
          scopeSceneIds,
          sceneSelection,
          packageCategories,
          uploadedAssets: project.uploadedAssets,
          sourceAssignments: scopedPlan.sourceAssignments,
          sourceMap,
          voicePlan: scopedPlan.voices,
          storyboardFrames: scopedPlan.storyboardFrames,
          uiMissingItems: packageMissingRequired,
          storyboardProvider: pkgCategories.includes("boards") ? "internal_svg" : "none",
          storyStyle: project.stylePreset,
          reviewGateState: pkgGateState,
          agentStrategy: packageAgentStrategy,
          packageIntent: intent,
          liveSendEnabled: false,
          providerSpendAllowed: false
        }
      });
      const packageWithSourceMap = {
        ...result,
        sourceMap: packageSourceMapFromPackage(result) || sourceMap
      };
      const localPackageItem = createPackageHistoryItem(packageWithSourceMap, project, pkgScope, intent);
      let packageItem = localPackageItem;
      let savedInCloud = false;
      if (apiStatus === "online" && cloudStorageReady && accessKey) {
        try {
          const savedPackage = await saveProjectPackageVersion(project.id, accessKey, localPackageItem);
          if (savedPackage.ok === false) throw new Error(savedPackage.message || "Package version save failed.");
          if (savedPackage.package) packageItem = savedPackage.package;
          const savedAt = stringValue(savedPackage.meta?.updatedAt);
          if (savedAt) setCloudVersion(savedAt);
          savedInCloud = true;
        } catch (packageError) {
          setNotice(`Cloud package version save failed: ${packageError instanceof Error ? packageError.message : String(packageError)}. Browser package history is still saved.`);
        }
      }
      setProject((current) => withProductionPackageHistoryItem(current, packageItem));
      setNotice(intent === "send_test"
        ? `480p dry-run test package saved${savedInCloud ? " to cloud history" : ""}. EvoLink was not called.`
        : `Dry-run production package saved${savedInCloud ? " to cloud history" : ""}. EvoLink was not called.`
      );
      return true;
    } catch (error) {
      const localPlan = scopedPlan || filmProjectToApiPlan(project);
      const runtimeChunks = recordArrayValue(localPlan.chunks);
      const reviewGateBlockers = Object.entries(pkgGateState)
        .filter(([, value]) => !value)
        .map(([key]) => `gate: ${key}`);
      const localMissingBlockers = missingRequiredBlockerLabels(packageMissingRequired);
      const localBlockedReasons = [...localMissingBlockers, ...reviewGateBlockers];
      const localPackageReady = localBlockedReasons.length === 0;
      const fallbackPackage = {
          schema: "kinetix_film.production_package.local_fallback.v1",
          dryRunOnly: true,
          notExecuted: ["evolink", "seedance"],
          createdAt: packageCreatedAt,
          status: packageMissingRequired.length
            ? "missing_required_inputs"
            : localPackageReady
              ? "local_package_ready"
              : "review_gate_incomplete",
          resolution: project.resolution,
          aspect: project.aspectRatio,
          packageIntent: intent,
          reviewGate: {
            state: pkgGateState,
            complete: Object.values(pkgGateState).every(Boolean)
          },
          scope: {
            type: pkgScope,
            sceneIds: scopeSceneIds,
            sceneSelection
          },
          sceneSelection,
          targetSceneIds: scopeSceneIds,
          categories: packageCategories,
          sourceMap,
          uiMissingItems: packageMissingRequired,
          missingRequired: packageMissingRequired,
          runtime: {
            chunks: runtimeChunks,
            chunkCount: runtimeChunks.length,
            scopeSeconds: runtimeChunks.reduce((total, chunk) => total + numberValue(chunk.durationSeconds || chunk.seconds), 0)
          },
          liveSendEnabled: false,
          providerSpendAllowed: false,
          agentStrategy: packageAgentStrategy,
          providerPayload: {
            provider: "evolink",
            version: "local-fallback.dry-run.v1",
            ready: localPackageReady,
            blocked: localBlockedReasons,
            liveSendEnabled: false,
            providerSpendAllowed: false,
            payload: null
          },
          plan: localPlan
      };
      const localPackageItem = createPackageHistoryItem(fallbackPackage, project, pkgScope, intent);
      let packageItem = localPackageItem;
      if (apiStatus === "online" && cloudStorageReady && accessKey) {
        try {
          const savedPackage = await saveProjectPackageVersion(project.id, accessKey, localPackageItem);
          if (savedPackage.ok === false) throw new Error(savedPackage.message || "Package version save failed.");
          if (savedPackage.package) packageItem = savedPackage.package;
          const savedAt = stringValue(savedPackage.meta?.updatedAt);
          if (savedAt) setCloudVersion(savedAt);
        } catch {
          // The browser-local package history remains the source of truth when cloud package storage is unavailable.
        }
      }
      setProject((current) => withProductionPackageHistoryItem(current, packageItem));
      setNotice(`Package API unavailable: ${error instanceof Error ? error.message : String(error)}. Local dry-run package saved.`);
      return false;
    } finally {
      setIsSavingPackage(false);
    }
  }

  function handleRenameProductionPackage(packageId: string, title: string) {
    const cleanTitle = title.trim();
    if (!cleanTitle) {
      setNotice("Package name cannot be blank.");
      return;
    }
    setProject((current) => ({
      ...current,
      productionPackages: (current.productionPackages || []).map((item) => (
        item.id === packageId ? { ...item, title: cleanTitle, renamedAt: new Date().toISOString() } : item
      ))
    }));
    setNotice("Package renamed.");
  }

  function handleDeleteProductionPackage(packageId: string) {
    setProject((current) => {
      const nextPackages = (current.productionPackages || []).filter((item) => item.id !== packageId);
      return {
        ...current,
        lastProductionPackage: nextPackages[0]?.package,
        productionPackages: nextPackages
      };
    });
    setNotice("Package deleted from history.");
  }

  async function handleDuplicateProductionPackage(packageId: string) {
    const packages = project.productionPackages || [];
    const source = packages.find((item) => item.id === packageId);
    if (!source) {
      setNotice("Package not found.");
      return;
    }
    let duplicate = duplicateProductionPackageItem(source, packages.length + 1);
    let duplicatedInCloud = false;
    if (apiStatus === "online" && cloudStorageReady && accessKey) {
      try {
        const response = await duplicateProjectPackageVersion(project.id, packageVersionReference(source), accessKey, `${source.title} copy`);
        if (response.ok === false) throw new Error(response.message || "Package duplicate failed.");
        if (response.package) duplicate = response.package;
        const savedAt = stringValue(response.meta?.updatedAt);
        if (savedAt) setCloudVersion(savedAt);
        duplicatedInCloud = true;
      } catch (error) {
        setNotice(`Cloud duplicate failed: ${error instanceof Error ? error.message : String(error)}. Browser package history is still saved.`);
      }
    }
    setProject((current) => withProductionPackageHistoryItem(current, duplicate));
    setNotice(`Package duplicated as the latest version${duplicatedInCloud ? " in cloud history" : ""}.`);
  }

  async function handleFrameUpload(file: File) {
    if (!selectedFrame) return;
    const frameId = selectedFrame.id;
    const dataUrl = await readFileAsDataUrl(file);
    const localRecord = buildLocalAssetRecord("sceneReferences", file, 0, {
      kind: "storyboard_frame",
      label: `${activeScene.slugline} - ${selectedFrame.shotType}`,
      sceneId: activeScene.id,
      sourceOfTruth: true,
      locked: true,
      sourceMode: "user_upload",
      provider: "user"
    });

    updateFrame(frameId, (frame) => ({
      ...frame,
      imageUrl: dataUrl,
      sourceUrl: dataUrl,
      source: "uploaded",
      locked: true,
      queuedRegenerate: false,
      disabled: false,
      generationStatus: "uploaded",
      generationJobId: undefined,
      generationError: undefined,
      fileName: file.name,
      cloudStatus: "pending"
    }));
    setNotice(`${file.name} uploaded locally. Persisting storyboard source material...`);

    try {
      const response = await uploadProjectAsset(project.id, file, localRecord);
      const uploaded = uploadedRecordFromResponse(localRecord, response);
      updateFrame(frameId, (frame) => ({
        ...frame,
        imageUrl: uploaded.sourceUrl || frame.imageUrl,
        sourceUrl: uploaded.sourceUrl || frame.sourceUrl,
        storageKey: uploaded.storageKey,
        cloudStatus: uploaded.cloudStatus,
        cloudMessage: uploaded.cloudMessage,
        fileName: uploaded.fileName || frame.fileName
      }));
      setNotice(
        uploaded.cloudStatus === "uploaded"
          ? `${file.name} uploaded, locked, and saved as storyboard source material.`
          : `${file.name} locked on the storyboard. It will stay local until R2 is available.`
      );
    } catch (error) {
      updateFrame(frameId, (frame) => ({
        ...frame,
        cloudStatus: "local_only",
        cloudMessage: error instanceof Error ? error.message : String(error)
      }));
      setNotice(`${file.name} locked on the storyboard. Upload fallback used: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  function renderStatusLine() {
    const statusClass = draftConflict ? " needs-action" : saveStatus === "Save failed" ? " error" : "";
    return (
      <div className={`status-line${statusClass}`} role="status" aria-live="polite">
        <span>{notice}</span>
        {draftConflict ? (
          <div className="status-actions" role="group" aria-label="Resolve draft conflict">
            <button className="secondary-action compact-action" onClick={handleRestoreCloudDraft} type="button">
              Restore Cloud Draft
            </button>
            <button className="primary-action compact-action" onClick={handleKeepLocalDraft} type="button">
              Keep Browser Draft
            </button>
          </div>
        ) : null}
        {!draftConflict && saveStatus === "Save failed" ? (
          <div className="status-actions" role="group" aria-label="Retry cloud autosave">
            <button className="secondary-action compact-action" onClick={handleKeepBrowserDraftAfterSaveFailure} type="button">
              Keep Browser Draft
            </button>
            <button className="primary-action compact-action" onClick={handleRetryCloudAutosave} type="button">
              Retry Cloud Save
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  function renderMobileStickyActions() {
    if (workflowView !== "workspace") return null;
    if (showReview) {
      return (
        <div className="mobile-sticky-actions" aria-label="Mobile review actions">
          <button className="secondary-action" onClick={() => setShowReview(false)} type="button">
            Review Later
          </button>
          <button className="primary-action" disabled={isSavingPackage} onClick={() => void handleSaveDryRunPackage("save")} type="button">
            {isSavingPackage ? "Saving Package" : "Save Package"}
          </button>
        </div>
      );
    }
    if (sourceFocus) {
      return (
        <div className="mobile-sticky-actions" aria-label="Mobile source repair actions">
          <button className="secondary-action" onClick={() => setShowImportModal(true)} type="button">
            Import Asset
          </button>
          <button
            className="primary-action"
            onClick={() => {
              setShowReview(true);
              setSourceFocus(null);
            }}
            type="button"
          >
            Return to Review
          </button>
        </div>
      );
    }
    if (activeSection !== "storyboards") return null;
    return (
      <div className="mobile-sticky-actions" aria-label="Mobile storyboard actions">
        <button
          className="secondary-action"
          onClick={() => {
            setActiveSection("storyboards");
            setShowReview(false);
            setShowGenerate(true);
          }}
          type="button"
        >
          Generate Boards
        </button>
        <button className="primary-action" onClick={() => setShowReview(true)} type="button">
          Review EvoLink
        </button>
      </div>
    );
  }

  if (experience === "simple") {
    return (
      <SimpleMode
        project={project}
        isAnalyzing={isAnalyzing}
        isEasyModeRunning={isEasyModeRunning}
        easyModeProgress={easyModeProgress}
        easyModeOptions={easyModeOptions}
        onDropScript={(file) => {
          handleScriptUpload(file).catch((error) => setNotice(`Script upload failed: ${error instanceof Error ? error.message : String(error)}`));
        }}
        onAnalyze={() => void handleAnalyzeScript({ autoApprove: true })}
        onJustMakeIt={handleJustMakeIt}
        onTrySample={handleTrySample}
        isJustMaking={justMakePending || samplePending}
        apiStatus={apiStatus}
        engineStale={engineStale}
        onMakeFilm={(options, extras) => {
          setEasyModeOptions(options);
          if (extras?.stylePreset || extras?.runtimeSeconds) {
            updateProject({
              ...(extras.stylePreset ? { stylePreset: extras.stylePreset } : {}),
              ...(extras.runtimeSeconds ? { runtimeSeconds: extras.runtimeSeconds } : {})
            });
          }
          void handleEasyMode(options);
        }}
        onRemakeScene={handleSimpleSceneRemake}
        onRestoreTake={handleRestoreTake}
        onSaveSceneNote={handleSaveSceneNote}
        onShareLink={handleShareLink}
        isSharingLink={isSharingLink}
        onMakePoster={() => void handleMakePoster()}
        isMakingPoster={isMakingPoster}
        onUploadPersonPhoto={(personName, files) => {
          handleAssetUpload("characterCards", files, {
            kind: "character_card",
            characterName: personName,
            label: files.length === 1 ? `${personName} — your photo` : undefined
          });
        }}
        onUploadPlacePhotos={(files) => {
          handleAssetUpload("sceneReferences", files, { kind: "scene_reference" });
        }}
        onUploadAssets={(bucket, files) => {
          handleAssetUpload(
            bucket,
            files,
            bucket === "characterCards" ? { kind: "character_card" } : bucket === "sceneReferences" ? { kind: "scene_reference" } : {}
          );
        }}
        onOpenPro={() => setExperience("pro")}
        onSendToRender={handleSimplePackage}
        isSavingPackage={isSavingPackage}
        notice={notice}
        films={cloudProjects}
        isLoadingFilms={isLoadingProjects}
        onRefreshFilms={() => void refreshCloudProjects()}
        onOpenFilm={(projectId) => void handleOpenSavedProject(projectId)}
        onNewFilm={handleNewProject}
        onNewFromImages={handleNewFromImages}
      />
    );
  }

  if (!proClassic) {
    return (
      <>
      <ProGlass
        project={project}
        theme={theme}
        onThemeChange={setTheme}
        onOpenLibrary={() => setShowAssetLibrary(true)}
        onSmartIngest={(files) => void handleSmartIngest(files)}
        onSaveCutPlans={(store) => updateProject({ cutPlans: store as unknown as FilmProject["cutPlans"] })}
        onPatchProject={(patch) => updateProject(patch)}
        onMakePoster={() => void handleMakePoster()}
        isMakingPoster={isMakingPoster}
        apiStatus={apiStatus}
        engineStale={engineStale}
        saveStatus={saveStatus}
        isAnalyzing={isAnalyzing}
        isEasyModeRunning={isEasyModeRunning}
        isGeneratingBoards={isGeneratingBoards}
        isSavingPackage={isSavingPackage}
        isSharingLink={isSharingLink}
        notice={notice}
        onOpenSimple={() => setExperience("simple")}
        onJustMakeIt={handleJustMakeIt}
        onScriptUpload={(file) => {
          handleScriptUpload(file).catch((error) => setNotice(`Script upload failed: ${error.message || String(error)}`));
        }}
        onAnalyze={() => void handleAnalyzeScript({ autoApprove: true })}
        onGenerateScene={handleGenerateScene}
        onRemakeScene={handleSimpleSceneRemake}
        onRestoreTake={handleRestoreTake}
        onSaveSceneNote={handleSaveSceneNote}
        onSetVoicePreset={handleSetVoicePreset}
        onUploadPersonPhoto={(personName, files) => {
          handleAssetUpload("characterCards", files, {
            kind: "character_card",
            characterName: personName,
            label: files.length === 1 ? `${personName} — your photo` : undefined
          });
        }}
        onUploadAssets={(bucket, files) => {
          const kinds: Record<string, string> = {
            characterCards: "character_card",
            sceneReferences: "scene_reference",
            voiceAudio: "voice_audio",
            music: "music",
            sfx: "sfx",
            videoReferences: "video_reference",
            styleRefs: "style_reference",
            branding: "branding",
            documents: "document"
          };
          handleAssetUpload(bucket, files, kinds[bucket] ? { kind: kinds[bucket] } : {});
        }}
        onShareLink={handleShareLink}
        onPackage={(gates) => handleSaveDryRunPackage("save", { scope: "all", gateState: gates })}
      />
      {showAssetLibrary ? (
        <AssetLibraryDrawer
          project={project}
          onClose={() => setShowAssetLibrary(false)}
          onToggleAssetDisabled={handleToggleLibraryAssetDisabled}
          onDeleteAsset={handleDeleteAsset}
          onRenameAsset={handleRenameAsset}
          onDuplicateAsset={handleDuplicateAsset}
          onMoveAsset={handleMoveAsset}
          onUploadAssets={handleAssetUpload}
          onRetryAssetUpload={handleRetryAssetUpload}
        />
      ) : null}
    </>
    );
  }

  return (
    <AppShell
      theme={theme}
      activeSection={activeSection}
      onSectionChange={(section) => {
        setActiveSection(section);
        setShowReview(false);
        setShowGenerate(false);
        setShowPromptPreview(false);
        setShowAssetLibrary(false);
        setWorkflowView("workspace");
      }}
      onThemeChange={setTheme}
    >
      <main className="workspace">
        {workflowView === "intake" ? (
          <ProjectIntake
            project={project}
            scriptUpload={project.scriptUpload}
            isAnalyzing={isAnalyzing}
            onProjectChange={updateProject}
            onScriptUpload={(file) => {
              handleScriptUpload(file).catch((error) => setNotice(`Script upload failed: ${error.message || String(error)}`));
            }}
            onAnalyze={handleAnalyzeScript}
            onOpenImport={() => setShowImportModal(true)}
            onClose={() => setWorkflowView("workspace")}
          />
        ) : workflowView === "parseReview" && pendingAnalysis && pendingProject ? (
          <AIParseReview
            analysis={pendingAnalysis}
            project={pendingProject}
            isAnalyzing={isAnalyzing}
            onApprove={handleApproveParse}
            onBack={() => setWorkflowView("intake")}
            onRerun={handleAnalyzeScript}
            onResolveMissingInput={handleResolveMissingInput}
          />
        ) : workflowView === "aiPlan" && pendingAnalysis && pendingProject ? (
          <AIPlanReview
            analysis={pendingAnalysis}
            project={pendingProject}
            onApprove={handleApprovePlan}
            onBack={() => setWorkflowView("parseReview")}
            onResolveMissingInput={handleResolveMissingInput}
          />
        ) : (
          <>
            <Topbar
              project={project}
              apiStatus={apiStatus}
              saveStatus={saveStatus}
              uploadSync={uploadSync}
              syncActionState={draftConflict ? "conflict" : saveStatus === "Save failed" ? "failed" : undefined}
              onProjectChange={updateProject}
              onSelectScene={handleSearchSceneSelect}
              onCopyProjectLink={handleCopyProjectLink}
              onOpenAssetLibrary={() => setShowAssetLibrary(true)}
              onRestoreCloudDraft={handleRestoreCloudDraft}
              onKeepBrowserDraft={draftConflict ? handleKeepLocalDraft : handleKeepBrowserDraftAfterSaveFailure}
              onRetryCloudSave={handleRetryCloudAutosave}
              onOpenSetup={() => setWorkflowView("intake")}
              onOpenSimple={() => setExperience("simple")}
              onOpenGenerate={() => {
                setActiveSection("storyboards");
                setShowReview(false);
                setShowGenerate(true);
              }}
              onEasyMode={() => void handleEasyMode()}
              easyModeOptions={easyModeOptions}
              onEasyModeOptionsChange={setEasyModeOptions}
              isEasyModeRunning={isEasyModeRunning}
              onOpenReview={() => setShowReview(true)}
            />

            {easyModeProgress ? (
              <EasyModeProgressPanel
                progress={easyModeProgress}
                onDismiss={() => setEasyModeProgress(null)}
                onOpenStoryboards={() => {
                  setEasyModeProgress(null);
                  setActiveSection("storyboards");
                  setShowReview(false);
                  setShowGenerate(false);
                  setWorkflowView("workspace");
                }}
              />
            ) : null}

            {activeSection === "projects" && !showReview ? (
              <div className="source-page">
                <ProjectLibrary
                  currentProject={project}
                  projects={cloudProjects}
                  apiStatus={apiStatus}
                  cloudStorageReady={cloudStorageReady}
                  isLoading={isLoadingProjects}
                  message={projectLibraryMessage}
                  onRefresh={refreshCloudProjects}
                  onOpenProject={(projectId) => {
                    void handleOpenSavedProject(projectId);
                  }}
                  onReviewPackage={(projectId) => {
                    void handleReviewProjectPackage(projectId);
                  }}
                  onDuplicateProject={(projectId) => {
                    void handleDuplicateProject(projectId);
                  }}
                  onDeleteProject={(projectId) => {
                    void handleDeleteProject(projectId);
                  }}
                  onNewProject={handleNewProject}
                  onBackToWorkspace={() => {
                    setActiveSection("storyboards");
                    setShowReview(false);
                    setShowGenerate(false);
                  }}
                />
                {renderStatusLine()}
              </div>
            ) : (activeSection === "characters" || activeSection === "voices" || activeSection === "references") && !showReview ? (
              <div className="source-page">
                <SourceWorkspace
                  project={project}
                  activeSection={activeSection}
                  onSectionChange={setActiveSection}
                  onProjectChange={updateProject}
                  onUploadAssets={handleAssetUpload}
                  onRetryAssetUpload={handleRetryAssetUpload}
                  onParseReference={handleParseReferenceAsset}
                  parsingReferenceId={parsingReferenceId}
                  focus={sourceFocus}
                  onReturnFromFocus={handleReturnFromSourceFocus}
                />
                {renderStatusLine()}
              </div>
            ) : !activeScene || !selectedFrame ? (
              <div className="empty-app">No project scenes available.</div>
            ) : (
            <div className={`workspace-grid ${showReview ? "review-mode" : ""}`}>
              {showReview ? (
            <div className="review-workspace">
              <ReviewEvoLinkPanel
                project={project}
                selectedSceneId={activeScene.id}
                scope={reviewScope}
                categories={reviewCategories}
                gateState={reviewGateState}
                onScopeChange={setReviewScope}
                onCategoriesChange={setReviewCategories}
                onGateStateChange={setReviewGateState}
                onResolveMissingInput={handleResolveMissingInput}
                onSavePackage={handleSaveDryRunPackage}
                onRenamePackage={handleRenameProductionPackage}
                onDuplicatePackage={handleDuplicateProductionPackage}
                onDeletePackage={handleDeleteProductionPackage}
                isSavingPackage={isSavingPackage}
                onClose={() => setShowReview(false)}
              />
              {renderStatusLine()}
            </div>
              ) : (
                <>
              <ScriptRail
                project={project}
                selectedSceneId={activeScene.id}
                scriptUpload={project.scriptUpload}
                isAnalyzing={isAnalyzing}
                onSelectScene={handleSelectScene}
                onScriptUpload={(file) => {
                  handleScriptUpload(file).catch((error) => setNotice(`Script upload failed: ${error.message || String(error)}`));
                }}
                onAnalyze={handleAnalyzeScript}
              />

              <div className="main-column">
                {showGenerate ? (
                  <GenerateBoardsSheet
                    project={project}
                    settings={boardSettings}
                    onChange={setBoardSettings}
                    onClose={() => setShowGenerate(false)}
                    onGenerate={handleGenerateBoards}
                    isGenerating={isGeneratingBoards}
                    selectedSceneId={activeScene.id}
                    jobs={project.generationJobs || []}
                    onRetryJob={handleRetryGenerationJob}
                    onUploadInstead={handleUploadInsteadForGenerationJob}
                    onFixPreflight={(item) => handleResolveMissingInput(item, { returnTo: "generate" })}
                    onFixScript={() => {
                      setShowGenerate(false);
                      setShowReview(false);
                      setActiveSection("storyboards");
                      setNotice("Use Upload Script in the script rail, then Analyze to refresh the storyboard plan.");
                    }}
                  />
                ) : null}

                <StoryboardWorkspace
                  project={project}
                  scenes={project.scenes}
                  activeScene={activeScene}
                  selectedFrame={selectedFrame}
                  selectedFrameId={selectedFrame.id}
                  onSelectScene={handleSelectScene}
                  onSelectFrame={setSelectedFrameId}
                  onToggleLock={() => {
                    updateFrame(selectedFrame.id, (frame) => ({ ...frame, locked: !frame.locked, queuedRegenerate: frame.locked ? frame.queuedRegenerate : false }));
                    setNotice(selectedFrame.locked ? "Board unlocked." : "Board locked as source material.");
                  }}
                  onToggleQueue={() => {
                    updateFrame(selectedFrame.id, (frame) => {
                      const queued = !frame.queuedRegenerate;
                      return {
                        ...frame,
                        queuedRegenerate: queued,
                        generationStatus: queued ? "queued" : frame.source === "internal" ? "prompt_ready" : frame.generationStatus,
                        generationError: queued ? undefined : frame.generationError
                      };
                    });
                    setNotice(selectedFrame.queuedRegenerate ? "Board removed from regenerate queue." : "Board queued for next Generate Boards pass.");
                  }}
                  onToggleDisable={() => {
                    updateFrame(selectedFrame.id, (frame) => ({ ...frame, disabled: !frame.disabled, queuedRegenerate: false }));
                    setNotice(selectedFrame.disabled ? "Board enabled for EvoLink package." : "Board disabled for EvoLink package.");
                  }}
                  onClearFrame={() => {
                    updateFrame(selectedFrame.id, (frame) => ({
                      ...frame,
                      imageUrl: sampleProject.scenes.flatMap((scene) => scene.frames).find((sample) => sample.id === frame.id)?.imageUrl || frame.imageUrl,
                      source: "internal",
                      locked: false,
                      queuedRegenerate: false,
                      disabled: false,
                      generationStatus: "prompt_ready",
                      generationJobId: undefined,
                      generationError: undefined,
                      generatedAt: undefined,
                      fileName: undefined
                    }));
                    setNotice("Board override cleared.");
                  }}
                  onUploadFrame={handleFrameUpload}
                  onOpenPrompt={() => setShowPromptPreview(true)}
                  onOpenAssetLibrary={() => setShowAssetLibrary(true)}
                  onFrameSourceAssignmentsChange={handleFrameSourceAssignmentsChange}
                />

                <FrameStatesLegend />
                <StoryboardReviewList scenes={project.scenes} selectedSceneId={activeScene.id} onSelectScene={handleSelectScene} />

                {renderStatusLine()}
              </div>

              <div className="right-column">
                <Inspector
                  project={project}
                  scene={activeScene}
                  frame={selectedFrame}
                  onSceneDurationChange={handleSceneDurationChange}
                  onResetSceneChunks={handleResetSceneChunks}
                  onChunkDurationChange={handleChunkDurationChange}
                  onChunkToggle={handleChunkToggle}
                  onSplitChunk={handleSplitChunk}
                  onMergeChunk={handleMergeChunk}
                  onMoveChunk={handleMoveChunk}
                />
              </div>
                </>
              )}
            </div>
            )}
          </>
        )}
      </main>
      {showImportModal ? <ImportAssetsModal initialBucket={importInitialBucket} manifest={project.uploadedAssets} onClose={() => setShowImportModal(false)} onUploadAssets={handleAssetUpload} /> : null}
      {showPromptPreview && activeScene && selectedFrame ? (
        <PromptPreviewModal
          project={project}
          scene={activeScene}
          frame={selectedFrame}
          onClose={() => setShowPromptPreview(false)}
          onSavePrompt={handleSaveFramePrompt}
        />
      ) : null}
      {showAssetLibrary ? (
        <AssetLibraryDrawer
          project={project}
          activeSceneId={activeScene?.id}
          selectedFrameId={selectedFrame?.id}
          onClose={() => setShowAssetLibrary(false)}
          onUseFrame={selectedFrame ? handleUseLibraryFrame : undefined}
          onAttachReference={activeScene ? handleAttachLibraryReference : undefined}
          onToggleAssetDisabled={handleToggleLibraryAssetDisabled}
          onDeleteAsset={handleDeleteAsset}
          onRenameAsset={handleRenameAsset}
          onDuplicateAsset={handleDuplicateAsset}
          onMoveAsset={handleMoveAsset}
          onUploadAssets={handleAssetUpload}
          onRetryAssetUpload={handleRetryAssetUpload}
        />
      ) : null}
      {renderMobileStickyActions()}
    </AppShell>
  );
}
